import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, Scissors, Image, Sparkles, Play, Pause, Download, Loader2,
  CheckCircle, AlertCircle, ArrowRight, RotateCcw, RefreshCw,
  Monitor, X, ImagePlus, ChevronUp, ChevronDown, ChevronRight, Layers, Plus, Trash2, FileText, Archive, Video, Mic
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useCredits, CREDIT_OPERATIONS } from '@/hooks/useCredits';
import { useCreditConfirm } from '@/components/credits/CreditConfirmDialog';
import { UpgradeModal } from './subscription';
import { LoginModal } from './LoginModal';
import { RecordingModal } from './recording/RecordingModal';
import { CastingNetworksSection } from './CastingNetworksSection';
import { ToolShell, StepCard, StepHeader, type ToolStep } from './tool-shell';
import { HJTrimTimeline } from '@/components/ui/HJTrimTimeline';
import { HJSlider, HJToggle } from '@/components/hj';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'upload' | 'trim' | 'merge' | 'enhance' | 'audio' | 'background' | 'render' | 'download';

interface ClipInfo {
  id: string;
  file: File;
  name: string;
  objectUrl: string;
  s3Key: string;
  s3Url: string;
  duration: number;
  width: number;
  height: number;
  trimStart: string;
  trimEnd: string;
  trimmedS3Key: string;
  trimmedS3Url: string;
  status: 'uploading' | 'uploaded' | 'converting' | 'trimming' | 'trimmed' | 'error';
  uploadProgress?: number;
}

const STEPS: { key: WizardStep; label: string; icon: React.ElementType }[] = [
  { key: 'upload', label: 'Capture', icon: Video },
  { key: 'trim', label: 'Trim', icon: Scissors },
  { key: 'merge', label: 'Merge', icon: Layers },
  { key: 'enhance', label: 'Filter', icon: Sparkles },
  { key: 'audio', label: 'Audio', icon: Mic },
  { key: 'background', label: 'Background', icon: Image },
  { key: 'render', label: 'Render', icon: Video },
  { key: 'download', label: 'Download', icon: Download },
];

const STEP_ORDER: WizardStep[] = ['upload', 'trim', 'merge', 'enhance', 'audio', 'background', 'render', 'download'];

// Small component that generates a thumbnail on the fly from a video URL
// If initialThumbnail is provided, uses that instead of generating. Calls onGenerated when a new thumbnail is created.
function VideoThumbnail({ src, initialThumbnail, fallbackIcon, onGenerated }: {
  src: string; initialThumbnail?: string; fallbackIcon?: React.ReactNode; onGenerated?: (dataUrl: string) => void;
}) {
  const [thumb, setThumb] = useState<string | null>(initialThumbnail || null);
  const attempted = useRef(false);
  useEffect(() => {
    if (initialThumbnail || !src || attempted.current) return;
    attempted.current = true;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = () => { video.removeAttribute('src'); video.load(); };
    const timeout = setTimeout(cleanup, 10000);
    video.onloadedmetadata = () => { video.currentTime = Math.min(1, video.duration * 0.1); };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const isPortrait = video.videoHeight > video.videoWidth;
        canvas.width = isPortrait ? 180 : 320;
        canvas.height = isPortrait ? 320 : 180;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          setThumb(dataUrl);
          onGenerated?.(dataUrl);
        }
      } catch { /* ignore */ }
      clearTimeout(timeout); cleanup();
    };
    video.onerror = () => { clearTimeout(timeout); cleanup(); };
    video.src = src;
    return () => { clearTimeout(timeout); cleanup(); };
  }, [src, initialThumbnail]);
  if (thumb) return <img src={thumb} alt="" className="w-full h-full object-cover" />;
  return <>{fallbackIcon}</>;
}

// Build CSS filter string from color adjustment sliders for live preview
// These approximate the FFmpeg eq/colorbalance filters used at render time
function buildCssFilterPreview(adj: { brightness: number; contrast: number; saturation: number; temperature: number; exposure: number }): string {
  const parts: string[] = [];
  // CSS brightness: 0=black, 1=normal, 2=2x bright. Map -100..100 → 0.5..1.5
  if (adj.brightness !== 0) parts.push(`brightness(${1 + adj.brightness / 200})`);
  // CSS contrast: 0=grey, 1=normal, 2=2x. Map -100..100 → 0.5..1.5
  if (adj.contrast !== 0) parts.push(`contrast(${1 + adj.contrast / 200})`);
  // CSS saturate: 0=grey, 1=normal, 2=2x. Map -100..100 → 0..2
  if (adj.saturation !== 0) parts.push(`saturate(${1 + adj.saturation / 100})`);
  // Temperature: warm = slight sepia + hue shift, cool = hue shift opposite
  if (adj.temperature !== 0) {
    const t = adj.temperature / 100; // -1 to 1
    if (t > 0) {
      parts.push(`sepia(${(t * 0.2).toFixed(2)})`);
      parts.push(`hue-rotate(${(-t * 5).toFixed(1)}deg)`);
    } else {
      parts.push(`hue-rotate(${(-t * 15).toFixed(1)}deg)`);
    }
  }
  // Exposure: similar to brightness but with gamma curve. Approximate with brightness
  if (adj.exposure !== 0) parts.push(`brightness(${Math.pow(2, adj.exposure / 200).toFixed(3)})`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

// FFmpeg preset keys - if the selected filter is one of these, pre-process with FFmpeg
const FFMPEG_PRESET_KEYS = new Set([
  'cinematic-warm', 'cinematic-cool', 'teal-orange', 'vintage', 'film-noir',
  'bleach-bypass', 'vivid', 'sunset', 'soft-glow', 'high-contrast-bw',
]);

const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms}`;
};

const parseTimeToSeconds = (timeStr: string): number => {
  const parts = timeStr.replace(/[^\d:.]/g, '').split(':');
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s);
  }
  if (parts.length === 2) {
    const [m, s] = parts;
    return parseInt(m) * 60 + parseFloat(s);
  }
  return parseFloat(parts[0]) || 0;
};

// Small segmented control matching the design's Radio pattern (enhancer.jsx
// line 1082) — pill row with the active option getting a raised surface.
// Local to this file; not yet promoted to a shared primitive.
const SegmentedControl: React.FC<{
  options: { value: string; label: string }[];
  active: string;
  onChange: (v: string) => void;
}> = ({ options, active, onChange }) => (
  <div
    className="flex"
    style={{
      background: 'var(--hj-surface-2)',
      borderRadius: 'var(--hj-r-sm)',
      padding: 3,
      gap: 2,
      border: '1px solid var(--hj-hairline-soft)',
    }}
  >
    {options.map((o) => {
      const isActive = o.value === active;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className="flex-1 transition-colors"
          style={{
            padding: '8px 10px',
            background: isActive ? 'var(--hj-surface-3)' : 'transparent',
            border: 0,
            borderRadius: 'calc(var(--hj-r-sm) - 2px)',
            color: isActive ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
            fontSize: 12.5,
            fontWeight: isActive ? 600 : 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

export const AuditionEnhancer = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getAuthHeaders, getAuthToken } = useAuth();
  const { isSubscriber, canUseTools, loading: subscriptionLoading } = useSubscription();
  const { checkCredits, credits: tokenCount } = useCredits();
  const { confirmCredit, CreditConfirmDialog } = useCreditConfirm();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStep>>(new Set());

  // Multi-clip state
  const [clips, setClips] = useState<ClipInfo[]>([]);
  const [activeClipId, setActiveClipId] = useState<string>('');

  // Trim state
  const [trimClipIndex, setTrimClipIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoPreviewError, setVideoPreviewError] = useState(false);

  // Merge state
  const [mergedS3Key, setMergedS3Key] = useState('');
  const [mergedS3Url, setMergedS3Url] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergedDuration, setMergedDuration] = useState(0);
  const [mergeError, setMergeError] = useState('');
  const [previewClipId, setPreviewClipId] = useState<string | null>(null);

  // Background state
  const bgRemovalJobIdRef = useRef<string | null>(null);
  const bgRemovalIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [bgOption, setBgOption] = useState<'skip' | 'remove' | 'greenscreen'>('skip');
  const [bgRemovalStatus, setBgRemovalStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
  const [bgRemovalProgress, setBgRemovalProgress] = useState(0);
  const [bgRemovalMessage, setBgRemovalMessage] = useState('');
  const [maskUrl, setMaskUrl] = useState('');
  const [greenScreenUrl, setGreenScreenUrl] = useState('');
  const [normalizedSourceUrl, setNormalizedSourceUrl] = useState('');
  const [bgReplacementUrl, setBgReplacementUrl] = useState('');
  const [bgReplacementType, setBgReplacementType] = useState<'image' | 'video' | 'preset'>('image');
  const [bgUploadingFile, setBgUploadingFile] = useState(false);
  const [bgVideoDuration, setBgVideoDuration] = useState<number | null>(null);
  const [bgVideoLoop, setBgVideoLoop] = useState(true);

  // Filter state (was "Enhance")
  const [videoFilter, setVideoFilter] = useState<string>('none');
  const [colorAdjustments, setColorAdjustments] = useState({ brightness: 0, contrast: 0, saturation: 0, temperature: 0, exposure: 0 });
  const [colorGradeTab, setColorGradeTab] = useState<'presets' | 'custom'>('presets');
  const [enhanceAudio, setEnhanceAudio] = useState(false);
  const [audioEnhanceMode, setAudioEnhanceMode] = useState<'light' | 'natural' | 'strong'>('natural');
  const [audioPreviewLoading, setAudioPreviewLoading] = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [audioPreviewMode, setAudioPreviewMode] = useState<'light' | 'natural' | 'strong' | null>(null);
  // Real 10s color-filter preview (FFmpeg-rendered, vs the CSS approximation).
  // colorPreviewSig pins which filter+adjustments the preview reflects, so a
  // stale preview is replaced by the CSS approx + a re-render prompt.
  const [colorPreviewLoading, setColorPreviewLoading] = useState(false);
  const [colorPreviewUrl, setColorPreviewUrl] = useState<string | null>(null);
  const [colorPreviewSig, setColorPreviewSig] = useState<string | null>(null);
  // Real 3s background-replace preview (Replicate matte + local composite).
  const [bgPreviewLoading, setBgPreviewLoading] = useState(false);
  const [bgPreviewUrl, setBgPreviewUrl] = useState<string | null>(null);
  const [bgPreviewSig, setBgPreviewSig] = useState<string | null>(null);
  const [filterPreviews, setFilterPreviews] = useState<Array<{ key: string; label: string; description: string; category: string; preview: string }>>([]);
  const [filterPreviewsLoading, setFilterPreviewsLoading] = useState(false);
  const [filteredVideoUrl, setFilteredVideoUrl] = useState('');
  const [filteredVideoKey, setFilteredVideoKey] = useState('');
  const [applyingFilter, setApplyingFilter] = useState(false);
  const [filterProgress, setFilterProgress] = useState(0);

  // Render state
  const [renderStatus, setRenderStatus] = useState<'idle' | 'trimming' | 'merging' | 'bg_removing' | 'rendering' | 'color_grading' | 'audio_enhancing' | 'completed' | 'failed'>('idle');
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderError, setRenderError] = useState('');

  // Render-job recovery: persist pipeline state server-side so tab-close/reload can resume
  const renderJobIdRef = useRef<string | null>(null);

  // Download state
  const [finalVideoUrl, setFinalVideoUrl] = useState('');

  // Save to library state
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  const [projects, setProjects] = useState<{ _id: string; name: string }[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('default');
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Library state
  const [libraryItems, setLibraryItems] = useState<Array<{
    id: string; name: string; savedS3Url: string; savedS3Key: string; savedAt: string; projectName?: string; projectId?: string; thumbnailUrl?: string;
  }>>([]);
  const [showLibrary, setShowLibrary] = useState(true);
  const [libraryVideoUrl, setLibraryVideoUrl] = useState<string | null>(null);
  const [libraryVideoName, setLibraryVideoName] = useState('');

  // Scratch files — raw recordings/uploads persisted so state resets don't lose them
  const [scratchFiles, setScratchFiles] = useState<Array<{
    _id: string; s3Key: string; bucket: string; fileName: string; fileType: string | null;
    fileSize: number | null; source: 'recording' | 'upload'; duration: number | null;
    width: number | null; height: number | null; createdAt: string;
  }>>([]);
  const [showScratch, setShowScratch] = useState(true);

  // Track paid credit operations so restarts don't re-charge
  const [creditsPaidFor, setCreditsPaidFor] = useState<Set<string>>(new Set());

  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  const activeClip = clips.find(c => c.id === activeClipId);

  // Credit confirmation helper
  const isCreditUser = useCallback(() => !!getAuthToken(), [getAuthToken]);

  const requireCreditConfirmation = useCallback(async (
    operationType: string,
    operationName: string,
    onConfirm: () => void
  ) => {
    // Skip credit check if already paid in this session
    if (creditsPaidFor.has(operationType)) { onConfirm(); return; }
    if (!isCreditUser()) { onConfirm(); return; }
    const creditCheck = await checkCredits(operationType);
    if (creditCheck?.isFree) { onConfirm(); return; }
    if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
    if (creditCheck?.hasCredits) {
      confirmCredit(operationType as any, operationName, () => {
        setCreditsPaidFor(prev => new Set([...prev, operationType]));
        onConfirm();
      });
    } else {
      toast({ title: 'Not enough video tokens', description: 'Please purchase more video tokens to continue.', variant: 'destructive' });
    }
  }, [isCreditUser, checkCredits, isSubscriber, confirmCredit, toast, creditsPaidFor]);

  // ===== NAVIGATION =====
  const stepIndex = STEP_ORDER.indexOf(currentStep);

  const goToStep = (step: WizardStep) => {
    const targetIndex = STEP_ORDER.indexOf(step);
    if (targetIndex > stepIndex && !completedSteps.has(STEP_ORDER[targetIndex - 1])) return;
    if (targetIndex < stepIndex) {
      if (step === 'upload') { resetFromTrim(); setCurrentStep('upload'); return; }
      const stepsToReset = STEP_ORDER.slice(targetIndex + 1);
      if (stepsToReset.includes('trim')) resetFromTrim();
      else if (stepsToReset.includes('merge')) resetFromMerge();
      else if (stepsToReset.includes('enhance')) resetFromEnhance();
      else if (stepsToReset.includes('audio')) resetFromAudio();
      else if (stepsToReset.includes('background')) resetFromBackground();
    }
    setCurrentStep(step);
  };

  const advanceStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(STEP_ORDER[nextIndex]);
    }
  };

  // Get source video dimensions — pick the most common dimension across all
  // clips (mode), not clips[0]. Mirrors the backend concat normalize, which
  // resamples every clip to the mode dimensions. Using clips[0] caused render
  // mismatches when take 0 was off-spec (e.g. 1548×1080 outlier among four
  // 1920×1080 clips → Shotstack rendered at 1548×1080 while source/mask were
  // both 1920×1080, mask didn't fit. Natasha 2026-04-30).
  const getSourceDimensions = (): { width: number; height: number } => {
    const dimCounts = new Map<string, { width: number; height: number; count: number }>();
    for (const c of clips) {
      if (!c.width || !c.height) continue;
      const key = `${c.width}x${c.height}`;
      const entry = dimCounts.get(key);
      if (entry) entry.count++;
      else dimCounts.set(key, { width: c.width, height: c.height, count: 1 });
    }
    let best: { width: number; height: number; count: number } | null = null;
    for (const entry of dimCounts.values()) {
      if (!best || entry.count > best.count) best = entry;
    }
    if (best) return { width: best.width, height: best.height };
    return { width: 1920, height: 1080 };
  };

  // Detect aspect ratio category from dimensions
  const getAspectRatioCategory = (): '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | 'custom' => {
    const { width, height } = getSourceDimensions();
    const ratio = width / height;
    if (Math.abs(ratio - 1) < 0.05) return '1:1';
    if (Math.abs(ratio - 16 / 9) < 0.1) return '16:9';
    if (Math.abs(ratio - 9 / 16) < 0.1) return '9:16';
    if (Math.abs(ratio - 4 / 3) < 0.1) return '4:3';
    if (Math.abs(ratio - 3 / 4) < 0.1) return '3:4';
    return 'custom';
  };

  // Get Shotstack output config that preserves source aspect ratio
  const getShotstackOutputConfig = () => {
    const ar = getAspectRatioCategory();
    const { width, height } = getSourceDimensions();
    // For standard 16:9, use Shotstack's built-in 'hd' resolution
    if (ar === '16:9') return { format: 'mp4', resolution: 'hd', fps: 30, quality: 'high' };
    // For all other aspect ratios, preserve source dimensions exactly when they
    // already fit inside a 1920×1080 box. Only scale down when source is bigger
    // than that. The previous "longest edge = 1080" rule downsampled fine sources
    // (e.g. 1548×1080 became 1080×754, losing 30% horizontal pixels — Natasha 2026-04-30).
    let outW = width;
    let outH = height;
    const maxW = 1920;
    const maxH = 1080;
    if (outW > maxW || outH > maxH) {
      const scale = Math.min(maxW / outW, maxH / outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }
    outW = outW % 2 === 0 ? outW : outW + 1;
    outH = outH % 2 === 0 ? outH : outH + 1;
    return { format: 'mp4', size: { width: outW, height: outH }, fps: 30, quality: 'high' };
  };

  const resetAll = () => {
    clips.forEach(c => URL.revokeObjectURL(c.objectUrl));
    setClips([]);
    setActiveClipId('');
    resetFromTrim();
    // Clear BG removal results from previous session (resetFromBackground preserves these
    // for re-use within a session, but a full reset must clear them)
    setMaskUrl('');
    setGreenScreenUrl('');
    setNormalizedSourceUrl('');
    setBgRemovalStatus('idle');
    setBgRemovalProgress(0);
    setBgRemovalMessage('');
    setBgOption('skip');
    setCompletedSteps(new Set());
    setCurrentStep('upload');
  };

  const resetFromTrim = () => {
    setTrimClipIndex(0);
    setClips(prev => prev.map(c => ({
      ...c,
      trimStart: '00:00:00.0',
      trimEnd: formatTime(c.duration),
      trimmedS3Key: '',
      trimmedS3Url: '',
      status: 'uploaded' as const,
    })));
    resetFromMerge();
  };

  const resetFromMerge = () => {
    setMergedS3Key('');
    setMergedS3Url('');
    setMergedDuration(0);
    setMergeError('');
    setPreviewClipId(null);
    // Source video changed — invalidate BG removal results (mask is tied to the source)
    setMaskUrl('');
    setGreenScreenUrl('');
    setNormalizedSourceUrl('');
    setBgRemovalStatus('idle');
    resetFromEnhance();
  };

  const resetFromEnhance = () => {
    setFilteredVideoUrl('');
    setFilteredVideoKey('');
    setFilterPreviews([]);
    resetFromAudio();
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('enhance');
      next.delete('audio');
      next.delete('background');
      next.delete('render');
      next.delete('download');
      return next;
    });
  };

  const resetFromAudio = () => {
    setEnhanceAudio(false);
    setAudioPreviewUrl(null);
    setAudioPreviewMode(null);
    resetFromBackground();
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('audio');
      next.delete('background');
      next.delete('render');
      next.delete('download');
      return next;
    });
  };

  const resetFromBackground = () => {
    // Preserve Replicate results (maskUrl, greenScreenUrl, normalizedSourceUrl, bgRemovalStatus)
    // so the user doesn't have to re-run the expensive background removal job.
    // Only reset the background replacement choice.
    setBgReplacementUrl('');
    setBgReplacementType('image');
    setRenderStatus('idle');
    setRenderProgress(0);
    setRenderError('');
    setFinalVideoUrl('');
    setCompletedSteps(prev => {
      const next = new Set(prev);
      next.delete('background');
      next.delete('render');
      next.delete('download');
      return next;
    });
  };

  // ===== STEP 1: UPLOAD (MULTI) =====
  const uploadFileToS3 = async (file: File, clipId: string) => {
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fileName: file.name, fileType: file.type || 'video/mp4', ephemeral: true }),
      });
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, key, downloadUrl } = await res.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setClips(prev => prev.map(c => c.id === clipId ? { ...c, uploadProgress: pct } : c));
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });

      return { key, downloadUrl };
    } catch (err: any) {
      setClips(prev => prev.map(c => c.id === clipId ? { ...c, status: 'error' as const } : c));
      toast({ title: 'Upload failed', description: `${file.name}: ${err.message}`, variant: 'destructive' });
      return null;
    }
  };

  // MOV → MP4 conversion via AWS MediaConvert (handles HDR→SDR tone mapping)
  const convertMovClip = async (clipId: string, s3Key: string) => {
    try {
      const res = await fetch('/api/convert-mov', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3Key }),
      });
      if (!res.ok) throw new Error('Failed to start conversion');
      const data = await res.json();
      // Already converted once before — backend returns the cached MP4 immediately,
      // no MediaConvert job, no polling.
      if (data.status === 'completed') {
        applyConvertedClip(clipId, data);
        return;
      }
      pollConversion(clipId, data.jobId);
    } catch (err: any) {
      setClips(prev => prev.map(c => c.id === clipId ? { ...c, status: 'error' as const } : c));
      toast({ title: 'Conversion failed', description: err.message, variant: 'destructive' });
    }
  };

  // Swap a clip over to its converted MP4 once conversion completes (fresh or cached).
  const applyConvertedClip = (clipId: string, data: { outputKey: string; downloadUrl: string }) => {
    // Load metadata from the converted MP4 (Chrome can read H.264)
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = data.downloadUrl;
    let metadataHandled = false;
    const updateClipAsUploaded = (withMetadata: boolean) => {
      if (metadataHandled) return;
      metadataHandled = true;
      setClips(prev => prev.map(c =>
        c.id === clipId ? {
          ...c,
          s3Key: data.outputKey,
          s3Url: data.downloadUrl,
          objectUrl: `/api/s3/hey-joe-video-rotator/${data.outputKey}`,
          ...(withMetadata ? {
            duration: tempVideo.duration,
            width: tempVideo.videoWidth,
            height: tempVideo.videoHeight,
            trimEnd: formatTime(tempVideo.duration),
          } : {}),
          status: 'uploaded' as const,
        } : c
      ));
    };
    tempVideo.onloadedmetadata = () => updateClipAsUploaded(true);
    tempVideo.onerror = () => updateClipAsUploaded(false);
    // Fallback: if neither event fires within 10s, update anyway
    setTimeout(() => updateClipAsUploaded(false), 10000);
  };

  const pollConversion = async (clipId: string, jobId: string) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/convert-mov/status/${jobId}`);
        if (!res.ok) throw new Error('Failed to check conversion status');
        const data = await res.json();

        if (data.status === 'completed') {
          applyConvertedClip(clipId, data);
        } else if (data.status === 'failed') {
          setClips(prev => prev.map(c => c.id === clipId ? { ...c, status: 'error' as const } : c));
          toast({ title: 'MOV conversion failed', description: data.error, variant: 'destructive' });
        } else {
          // Still processing — poll again in 5 seconds
          setTimeout(poll, 5000);
        }
      } catch (err: any) {
        setClips(prev => prev.map(c => c.id === clipId ? { ...c, status: 'error' as const } : c));
        toast({ title: 'Conversion error', description: err.message, variant: 'destructive' });
      }
    };
    poll();
  };

  const onDrop = useCallback(async (acceptedFiles: File[], source: 'upload' | 'recording' = 'upload') => {
    // Require login + subscription before allowing uploads
    if (!getAuthToken()) {
      setShowLoginModal(true);
      return;
    }
    if (!subscriptionLoading && !isSubscriber) {
      setShowUpgradeModal(true);
      return;
    }

    const videoFiles = acceptedFiles.filter(f =>
      f.type.startsWith('video/') || ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some(ext => f.name.toLowerCase().endsWith(ext))
    );
    if (videoFiles.length === 0) {
      toast({ title: 'Invalid files', description: 'Please select video files.', variant: 'destructive' });
      return;
    }

    // Create clip entries
    const newClips: ClipInfo[] = videoFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      objectUrl: URL.createObjectURL(file),
      s3Key: '',
      s3Url: '',
      duration: 0,
      width: 0,
      height: 0,
      trimStart: '00:00:00.0',
      trimEnd: '00:00:00.0',
      trimmedS3Key: '',
      trimmedS3Url: '',
      status: 'uploading' as const,
    }));

    setClips(prev => [...prev, ...newClips]);
    if (!activeClipId && newClips.length > 0) setActiveClipId(newClips[0].id);

    // Upload each in parallel and detect duration
    for (const clip of newClips) {
      // Get duration
      const tempVideo = document.createElement('video');
      tempVideo.preload = 'metadata';
      tempVideo.src = clip.objectUrl;
      tempVideo.onloadedmetadata = () => {
        setClips(prev => prev.map(c =>
          c.id === clip.id ? { ...c, duration: tempVideo.duration, width: tempVideo.videoWidth, height: tempVideo.videoHeight, trimEnd: formatTime(tempVideo.duration) } : c
        ));
      };

      // Upload to S3
      const result = await uploadFileToS3(clip.file, clip.id);
      if (result) {
        // Record in scratch_files so React state loss doesn't orphan the upload (#lost-clips bug)
        fetch('/api/scratch-files', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            s3Key: result.key,
            bucket: 'hey-joe-video-rotator',
            fileName: clip.file.name,
            fileType: clip.file.type || null,
            fileSize: clip.file.size || null,
            source,
          }),
        }).then(() => fetchScratchFiles()).catch(() => {});

        const needsConversion = !clip.file.name.toLowerCase().endsWith('.mp4');
        if (needsConversion) {
          // Non-MP4 files (MOV, WebM, AVI, MKV) need MediaConvert conversion to normalized MP4
          setClips(prev => prev.map(c =>
            c.id === clip.id ? { ...c, s3Key: result.key, s3Url: result.downloadUrl, status: 'converting' as const } : c
          ));
          convertMovClip(clip.id, result.key);
        } else {
          setClips(prev => prev.map(c =>
            c.id === clip.id ? { ...c, s3Key: result.key, s3Url: result.downloadUrl, status: 'uploaded' as const } : c
          ));
        }
      }
    }
  }, [activeClipId, getAuthHeaders, getAuthToken, isSubscriber, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    disabled: false,
  });

  const removeClip = (clipId: string) => {
    const clip = clips.find(c => c.id === clipId);
    if (clip) URL.revokeObjectURL(clip.objectUrl);
    setClips(prev => prev.filter(c => c.id !== clipId));
    if (activeClipId === clipId) {
      const remaining = clips.filter(c => c.id !== clipId);
      setActiveClipId(remaining.length > 0 ? remaining[0].id : '');
    }
  };

  const allClipsUploaded = clips.length > 0 && clips.every(c => c.status === 'uploaded' || c.status === 'trimmed');

  // Lifted trim "Continue" — runs the apply-or-skip + auto-advance flow so
  // the toolbar / mobile action bar's Next button does the same thing as the
  // in-card button on the Trim step.
  const handleTrimAdvance = async () => {
    const clip = clips[trimClipIndex];
    if (!clip) return;
    const inSec = parseTimeToSeconds(clip.trimStart);
    const outSec = parseTimeToSeconds(clip.trimEnd);
    const handlesMoved = inSec > 0.5 || outSec < clip.duration - 0.5;
    if (handlesMoved) {
      const ok = await applyTrimToClip(clip.id);
      if (!ok) return;
    } else {
      skipTrimForClip(clip.id);
    }
    advanceTrimClip();
  };

  // Check if Shotstack render can be skipped (no compositing
  // or Shotstack filter needed). Declared up here — above the toolbar IIFE
  // below — because the IIFE calls it synchronously when computing the
  // render step's label. Leaving it further down put the const in the TDZ
  // every render and crashed the page when the user reached the render step.
  const canSkipShotstack = (): boolean => {
    // Check user's choices, not pipeline results (maskUrl/greenScreenUrl are empty before render)
    const hasBgWork = (bgOption === 'remove' && bgReplacementUrl) || bgOption === 'greenscreen';
    const hasShotstackFilter = videoFilter !== 'none' && !FFMPEG_PRESET_KEYS.has(videoFilter);
    return !hasBgWork && !hasShotstackFilter;
  };

  // Per-step shell-level Next behavior. Each ported step wires its own
  // advance handler / label / disabled state into ToolShell so the toolbar
  // and MobileActionBar Next buttons do the same thing as the step's
  // in-card Continue. `showAdvance: false` is for terminal steps where the
  // workspace owns its own action set.
  const currentStepAdvance: {
    onAdvance: () => void;
    label?: string;
    disabled?: boolean;
    showAdvance?: boolean;
  } = (() => {
    switch (currentStep) {
      case 'upload':
        return {
          onAdvance: advanceStep,
          label: 'Continue to Trim',
          disabled: !allClipsUploaded,
        };
      case 'trim': {
        const clip = clips[trimClipIndex];
        const inSec = clip ? parseTimeToSeconds(clip.trimStart) : 0;
        const outSec = clip ? parseTimeToSeconds(clip.trimEnd) : 0;
        const handlesMoved = clip ? inSec > 0.5 || outSec < clip.duration - 0.5 : false;
        const isTrimming = clip?.status === 'trimming';
        return {
          onAdvance: handleTrimAdvance,
          label: isTrimming ? 'Trimming…' : handlesMoved ? 'Trim & continue' : 'Use full clip',
          disabled: !clip || isTrimming,
        };
      }
      case 'merge':
        // Lazy-bind: mergeClips is declared later in the component body, so
        // capture its identifier at call time, not at IIFE-eval time.
        return {
          onAdvance: () => mergeClips(),
          label: merging ? 'Merging…' : clips.length === 1 ? 'Continue' : 'Merge & continue',
          disabled: merging,
        };
      case 'enhance':
        return {
          onAdvance: () => applyFilterAndAdvance(),
          label: applyingFilter ? `Applying… ${Math.round(filterProgress)}%` : 'Continue to Audio',
          disabled: applyingFilter,
        };
      case 'audio':
        return {
          onAdvance: advanceStep,
          label: enhanceAudio ? 'Continue with enhanced audio' : 'Continue to Background',
        };
      case 'background':
        return {
          onAdvance: advanceStep,
          label:
            bgOption === 'skip'
              ? 'Continue without changes'
              : bgOption === 'greenscreen'
                ? 'Continue with green screen'
                : 'Continue with background',
          disabled: bgOption === 'remove' && !bgReplacementUrl,
        };
      case 'render': {
        // The render step's primary action depends on status. The actual
        // startRender / retry functions are declared later — lazy-bind via
        // arrow wrappers.
        if (renderStatus === 'idle') {
          // Rendering is a paid tool (the trim/split step is gated too). Free/Reader
          // tier can't render, so show an upgrade CTA instead of a Render button that
          // 403s and surfaces the useless "Render failed".
          if (!subscriptionLoading && !canUseTools) {
            // Say why when they hold tokens: the plan, not the balance, is what's missing.
            return { onAdvance: () => setShowUpgradeModal(true), label: tokenCount > 0 ? `Get Hey Joe Plus to use your ${tokenCount} video token${tokenCount === 1 ? '' : 's'}` : 'Get Hey Joe Plus to render' };
          }
          return {
            onAdvance: () => startRender(),
            label: canSkipShotstack() ? 'Render video' : 'Render video · 1 video token',
          };
        }
        if (renderStatus === 'completed') {
          return { onAdvance: advanceStep, label: 'Continue to Download' };
        }
        if (renderStatus === 'failed') {
          return {
            onAdvance: () => { setRenderStatus('idle'); setRenderError(''); },
            label: 'Try again',
          };
        }
        // In flight — disable Next, label reflects current sub-stage
        const stageLabel: Record<string, string> = {
          trimming: 'Trimming…',
          merging: 'Merging…',
          bg_removing: 'Removing background…',
          rendering: 'Rendering…',
          color_grading: 'Color grading…',
          audio_enhancing: 'Enhancing audio…',
        };
        return { onAdvance: () => {}, label: stageLabel[renderStatus] || 'Working…', disabled: true };
      }
      case 'download':
        // Terminal step — workspace owns its actions (Save to Device,
        // Start Over, Save to Library). Hide the shell-level Next.
        return { onAdvance: () => {}, showAdvance: false };
      default:
        return { onAdvance: advanceStep };
    }
  })();

  // Load Google Fonts for title preview
  // Resume polling when page becomes visible again (iPad/Safari suspends timers in background)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && bgRemovalJobIdRef.current && bgRemovalIntervalRef.current) {
        console.log('[AuditionEnhancer] Page visible again — restarting bg removal poll');
        pollBgRemoval(bgRemovalJobIdRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Fetch projects when reaching download step
  useEffect(() => {
    if (currentStep === 'download') {
      const fetchProjects = async () => {
        const authToken = getAuthToken();
        if (!authToken) return;
        setLoadingProjects(true);
        try {
          const res = await fetch('/api/projects', {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            setProjects(data.projects || []);
          }
        } catch { /* ignore */ }
        finally { setLoadingProjects(false); }
      };
      fetchProjects();
      setSavedToLibrary(false);
    }
  }, [currentStep, getAuthToken]);

  // Fetch library items from all projects
  const fetchLibrary = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      const res = await fetch('/api/projects', {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (!res.ok) return;
      const { projects: allProjects } = await res.json();
      const items: typeof libraryItems = [];
      for (const proj of allProjects || []) {
        for (const render of proj.renders || []) {
          if (render.savedS3Url || render.savedS3Key) {
            items.push({
              id: render.id || render._id || `${proj._id}-${render.savedAt}`,
              name: render.name || 'Untitled',
              savedS3Url: render.savedS3Url || render.url || '',
              savedS3Key: render.savedS3Key || '',
              savedAt: render.savedAt || render.createdAt || '',
              projectName: proj.name,
              projectId: proj._id,
              thumbnailUrl: render.thumbnailUrl,
            });
          }
        }
      }
      items.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
      setLibraryItems(items);
    } catch {}
  }, [getAuthToken]);

  // One-time cleanup: legacy project_*_savedRenders keys were not user-scoped and
  // leaked saved renders across different accounts on the same browser.
  // Safe to purge — MongoDB is authoritative for saved renders.
  useEffect(() => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && /^project_.+_savedRenders$/.test(key)) localStorage.removeItem(key);
      }
    } catch {}
  }, []);

  // Load library on mount
  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  // Fetch scratch files (raw uploads/recordings within the 30-day retention window)
  const fetchScratchFiles = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      const res = await fetch('/api/scratch-files', {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (!res.ok) return;
      const { files } = await res.json();
      setScratchFiles(files || []);
    } catch {}
  }, [getAuthToken]);

  useEffect(() => {
    fetchScratchFiles();
  }, [fetchScratchFiles]);

  // On-mount recovery: if the user had a render in flight (or just completed within
  // the last 2hrs), hydrate state so they can pick up where they left off.
  useEffect(() => {
    const authToken = getAuthToken();
    if (!authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/render-jobs/active', {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const { job } = await res.json();
        if (!job || cancelled) return;

        renderJobIdRef.current = job.jobId;

        if (job.status === 'completed' && job.finalVideoUrl) {
          setFinalVideoUrl(job.finalVideoUrl);
          setRenderStatus('completed');
          setRenderProgress(100);
          setCompletedSteps(new Set(STEP_ORDER.slice(0, -1)));
          setCurrentStep('download');
          toast({ title: 'Previous render recovered', description: 'Your finished video is ready to download.' });
          return;
        }

        if (job.status === 'processing') {
          const shotstackRenderId = job.stages?.shotstack?.renderId;
          const shotstackUrl = job.stages?.shotstack?.url;
          // If Shotstack already finished, go straight to post-render
          if (shotstackUrl) {
            setRenderStatus('rendering');
            setRenderProgress(100);
            setCompletedSteps(new Set(['upload', 'trim', 'merge', 'enhance', 'audio', 'background']));
            setCurrentStep('render');
            postRenderPipeline(shotstackUrl);
            toast({ title: 'Resuming render', description: 'Finishing your previous render.' });
          } else if (shotstackRenderId) {
            // Resume polling the in-flight Shotstack job
            setRenderStatus('rendering');
            setRenderProgress(Math.max(60, job.progress || 0));
            setCompletedSteps(new Set(['upload', 'trim', 'merge', 'enhance', 'audio', 'background']));
            setCurrentStep('render');
            pollRenderStatus(shotstackRenderId);
            toast({ title: 'Resuming render', description: 'Reconnecting to your in-progress render.' });
          }
          // Earlier stages (trim/merge/filter/bg) — we don't attempt partial resumption;
          // the user can hit Render again if they want.
        }
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-attach a scratch file back into the enhancer as a new clip, then jump to Trim
  const reattachScratchFile = async (sf: typeof scratchFiles[number]) => {
    const clipId = Math.random().toString(36).substr(2, 9);
    const bucketPrefix = sf.bucket && sf.bucket !== 'hey-joe-videos' ? `${sf.bucket}/` : '';
    const proxyUrl = `/api/s3/${bucketPrefix}${sf.s3Key}`;

    const newClip: ClipInfo = {
      id: clipId,
      file: new File([], sf.fileName, { type: sf.fileType || 'video/mp4' }),
      name: sf.fileName,
      objectUrl: proxyUrl,
      s3Key: sf.s3Key,
      s3Url: proxyUrl,
      duration: sf.duration || 0,
      width: sf.width || 0,
      height: sf.height || 0,
      trimStart: '00:00:00.0',
      trimEnd: sf.duration ? formatTime(sf.duration) : '00:00:00.0',
      trimmedS3Key: '',
      trimmedS3Url: '',
      status: 'uploaded' as const,
    };

    setClips(prev => [...prev, newClip]);
    setActiveClipId(clipId);

    if (!sf.duration || !sf.width) {
      const tempVideo = document.createElement('video');
      tempVideo.preload = 'metadata';
      tempVideo.src = proxyUrl;
      tempVideo.onloadedmetadata = () => {
        setClips(prev => prev.map(c =>
          c.id === clipId ? {
            ...c,
            duration: tempVideo.duration,
            width: tempVideo.videoWidth,
            height: tempVideo.videoHeight,
            trimEnd: formatTime(tempVideo.duration),
          } : c
        ));
      };
    }

    const needsConversion = !sf.fileName.toLowerCase().endsWith('.mp4');
    if (needsConversion) {
      setClips(prev => prev.map(c =>
        c.id === clipId ? { ...c, status: 'converting' as const } : c
      ));
      convertMovClip(clipId, sf.s3Key);
    }

    toast({ title: 'Clip restored', description: `${sf.fileName} is back in your clips.` });
  };

  const deleteScratchFile = async (id: string) => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      await fetch(`/api/scratch-files/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      setScratchFiles(prev => prev.filter(sf => sf._id !== id));
    } catch {}
  };

  const handleSaveToLibrary = async () => {
    if (!finalVideoUrl) return;
    setSavingToLibrary(true);
    try {
      const baseName = clips.length > 0 ? clips[0].name.replace(/\.[^.]+$/, '') : 'audition';
      const fileName = `enhanced_${baseName}.mp4`;

      // Transfer video to permanent S3 storage
      const transferRes = await fetch('/api/transfer-to-s3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videoUrl: finalVideoUrl, fileName }),
      });
      if (!transferRes.ok) {
        const err = await transferRes.json().catch(() => ({}));
        throw new Error(err.details || err.error || 'Transfer failed');
      }
      const { s3Key, s3Url } = await transferRes.json();

      const authToken = getAuthToken();
      if (!authToken) throw new Error('Sign in required');

      const newRender = {
        id: Date.now().toString(),
        name: fileName,
        url: s3Url,
        savedS3Key: s3Key,
        savedS3Url: s3Url,
        savedAt: new Date().toISOString(),
      };

      // Resolve target project — if the user is on the built-in "Default"
      // option, find or lazily create a real "Default" project so the render
      // actually persists. Previous behavior silently dropped the save in
      // that case, leaving the library permanently empty for default users.
      const authHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` };
      let projectId: string | undefined;
      let existingRenders: Array<{ id?: string; savedS3Key?: string; savedS3Url?: string }> = [];

      if (selectedProject === 'default') {
        const existing = projects.find(p => p.name === 'Default');
        if (existing) {
          projectId = existing._id;
        } else {
          const createRes = await fetch('/api/projects', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ name: 'Default', renders: [newRender] }),
          });
          if (!createRes.ok) throw new Error('Failed to create Default project');
          const { project } = await createRes.json();
          projectId = project._id;
          existingRenders = [newRender]; // Already saved via POST
        }
      } else {
        const target = projects.find(p => p.name === selectedProject);
        if (!target) throw new Error(`Project "${selectedProject}" not found`);
        projectId = target._id;
      }

      // If we still need to PUT (default-existing OR named-project paths),
      // read the project's current renders and APPEND the new one. The
      // backend's PUT does $set on the renders array, so sending just the
      // new render would wipe out every prior save on this project.
      if (projectId && existingRenders.length === 0) {
        const detailRes = await fetch(`/api/projects/${projectId}`, { headers: authHeaders });
        if (detailRes.ok) {
          const { project } = await detailRes.json();
          existingRenders = Array.isArray(project?.renders) ? project.renders : [];
        }
        await fetch(`/api/projects/${projectId}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ renders: [...existingRenders, newRender] }),
        });
      }

      setSavedToLibrary(true);
      // Refresh the projects list too so future saves see the just-created
      // Default project.
      try {
        const pRes = await fetch('/api/projects', { headers: authHeaders });
        if (pRes.ok) {
          const { projects: allProjects } = await pRes.json();
          if (Array.isArray(allProjects)) setProjects(allProjects);
        }
      } catch {}
      fetchLibrary();
      toast({
        title: 'Saved to library',
        description: `Video saved to "${selectedProject === 'default' ? 'Default' : selectedProject}" project.`,
      });
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setSavingToLibrary(false);
    }
  };

  // ===== STEP 2: TRIM (PER CLIP) =====
  // Sync activeClipId with trimClipIndex on trim step
  useEffect(() => {
    if (currentStep === 'trim' && clips[trimClipIndex]) {
      setActiveClipId(clips[trimClipIndex].id);
    }
  }, [currentStep, trimClipIndex, clips]);

  // Detect video background duration when selected
  useEffect(() => {
    if (!bgReplacementUrl || bgReplacementType !== 'video') {
      setBgVideoDuration(null);
      return;
    }
    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.crossOrigin = 'anonymous';
    const onLoaded = () => {
      if (tempVideo.duration && isFinite(tempVideo.duration)) {
        setBgVideoDuration(tempVideo.duration);
      }
      tempVideo.removeEventListener('loadedmetadata', onLoaded);
      tempVideo.src = '';
    };
    tempVideo.addEventListener('loadedmetadata', onLoaded);
    tempVideo.src = bgReplacementUrl;
    return () => {
      tempVideo.removeEventListener('loadedmetadata', onLoaded);
      tempVideo.src = '';
    };
  }, [bgReplacementUrl, bgReplacementType]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip?.objectUrl) return;
    setVideoPreviewError(false);
    video.src = activeClip.objectUrl;
    video.load();
    const onTime = () => setCurrentTime(video.currentTime);
    const onEnd = () => setIsPlaying(false);
    const onError = () => setVideoPreviewError(true);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnd);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnd);
      video.removeEventListener('error', onError);
    };
  }, [activeClip?.id, activeClip?.objectUrl, currentStep]);

  // Load filter previews when entering the Filter step
  useEffect(() => {
    if (currentStep !== 'enhance' || filterPreviews.length > 0 || filterPreviewsLoading) return;
    const videoKey = getUnfilteredVideoKey();
    const videoUrl = getUnfilteredVideoUrl();
    if (!videoKey && !videoUrl) return;

    const fetchPreviews = async () => {
      setFilterPreviewsLoading(true);
      try {
        const authToken = getAuthToken();
        const res = await fetch('/api/filter/previews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
          body: JSON.stringify({ videoKey, videoUrl }),
        });
        if (res.ok) {
          const data = await res.json();
          setFilterPreviews(data.previews || []);
        }
      } catch (err) {
        console.error('[AuditionEnhancer] Failed to load filter previews:', err);
      } finally {
        setFilterPreviewsLoading(false);
      }
    };
    fetchPreviews();
  }, [currentStep]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) { video.pause(); } else { video.play(); }
    setIsPlaying(!isPlaying);
  };

  const seekTo = (time: number) => {
    if (videoRef.current) { videoRef.current.currentTime = time; setCurrentTime(time); }
  };

  const previewTrim = () => {
    if (!activeClip || !videoRef.current) return;
    const start = parseTimeToSeconds(activeClip.trimStart);
    videoRef.current.currentTime = start;
    videoRef.current.play();
    setIsPlaying(true);
    const end = parseTimeToSeconds(activeClip.trimEnd);
    const checkEnd = () => {
      if (videoRef.current && videoRef.current.currentTime >= end) {
        videoRef.current.pause();
        setIsPlaying(false);
        videoRef.current.removeEventListener('timeupdate', checkEnd);
      }
    };
    videoRef.current.addEventListener('timeupdate', checkEnd);
  };

  const updateClipTrim = (clipId: string, field: 'trimStart' | 'trimEnd', value: string) => {
    setClips(prev => prev.map(c => c.id === clipId ? { ...c, [field]: value } : c));
  };

  const applyTrimToClip = async (clipId: string): Promise<boolean> => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return false;

    const startSec = parseTimeToSeconds(clip.trimStart);
    const endSec = parseTimeToSeconds(clip.trimEnd);

    if (startSec >= endSec) {
      toast({ title: 'Invalid range', description: 'End time must be after start time.', variant: 'destructive' });
      return false;
    }

    // Just mark as trimmed — actual trim happens at render time
    const isFullVideo = startSec < 0.5 && Math.abs(endSec - clip.duration) < 0.5;
    setClips(prev => prev.map(c =>
      c.id === clipId ? {
        ...c,
        trimmedS3Key: isFullVideo ? c.s3Key : '', // empty = needs trim at render
        trimmedS3Url: isFullVideo ? c.s3Url : '',
        status: 'trimmed' as const,
      } : c
    ));
    return true;
  };

  const skipTrimForClip = (clipId: string) => {
    const clip = clips.find(c => c.id === clipId);
    if (clip && !clip.s3Key) {
      toast({ title: 'Upload incomplete', description: 'This clip is still uploading or converting. Please wait.', variant: 'destructive' });
      return;
    }
    setClips(prev => prev.map(c =>
      c.id === clipId ? { ...c, trimmedS3Key: c.s3Key, trimmedS3Url: c.s3Url, status: 'trimmed' as const } : c
    ));
  };

  const allClipsTrimmed = clips.length > 0 && clips.every(c => c.status === 'trimmed');

  const advanceTrimClip = () => {
    if (trimClipIndex < clips.length - 1) {
      setTrimClipIndex(prev => prev + 1);
      setIsPlaying(false);
      setCurrentTime(0);
    } else {
      // All clips done, advance to merge
      advanceStep();
    }
  };

  // ===== STEP 3: MERGE =====
  const moveClip = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= clips.length) return;
    setClips(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr;
    });
  };

  const getTrimmedDuration = (clip: ClipInfo) => {
    const start = parseTimeToSeconds(clip.trimStart);
    const end = parseTimeToSeconds(clip.trimEnd);
    return end - start;
  };

  const mergeClips = async () => {
    // Just set duration and advance — actual merge happens at render time
    const totalDuration = clips.reduce((sum, c) => sum + getTrimmedDuration(c), 0);
    setMergedDuration(totalDuration);
    advanceStep();
  };

  // ===== SOURCE VIDEO HELPERS =====
  const getSourceVideoUrl = () => filteredVideoUrl || mergedS3Url || (clips.length === 1 ? clips[0]?.trimmedS3Url || clips[0]?.s3Url : '');
  const getSourceVideoKey = () => filteredVideoKey || mergedS3Key || (clips.length === 1 ? clips[0]?.trimmedS3Key || clips[0]?.s3Key : '');
  // Unfiltered source (for filter preview generation — always the merge/trim output, never the filtered version)
  // For filter previews — use merged if available, otherwise first clip (merge is deferred so mergedS3Url may be empty)
  const getUnfilteredVideoUrl = () => mergedS3Url || clips[0]?.trimmedS3Url || clips[0]?.s3Url || '';
  const getUnfilteredVideoKey = () => mergedS3Key || clips[0]?.trimmedS3Key || clips[0]?.s3Key || '';

  const startBgRemoval = async () => {
    const videoUrlToProcess = getSourceVideoUrl();
    const videoKeyToProcess = getSourceVideoKey();

    // Use 'green-screen' output type when user wants green screen directly from Replicate
    // This avoids the Shotstack luma-composite approach which produces green line artifacts
    const outputType = bgOption === 'greenscreen' ? 'green-screen' : 'foreground-mask';

    setBgRemovalStatus('processing');
    setBgRemovalProgress(0);
    setBgRemovalMessage('Starting background removal...');
    try {
      const authToken = getAuthToken();
      const res = await fetch('/api/remove-video-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ videoUrl: videoUrlToProcess, videoKey: videoKeyToProcess, outputType }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.details || 'Background removal failed');
      }

      const { jobId } = await res.json();
      bgRemovalJobIdRef.current = jobId;
      toast({ title: 'Background removal started', description: 'This may take a few minutes...' });
      pollBgRemoval(jobId);
    } catch (err: any) {
      setBgRemovalStatus('failed');
      toast({ title: 'Background removal failed', description: err.message, variant: 'destructive' });
    }
  };

  const pollBgRemoval = (jobId: string) => {
    // Clear any existing interval before starting a new one
    if (bgRemovalIntervalRef.current) clearInterval(bgRemovalIntervalRef.current);

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/remove-video-background/status/${jobId}`);
        const data = await res.json();
        // Update progress from backend
        if (data.progress !== undefined) setBgRemovalProgress(data.progress);
        if (data.message) setBgRemovalMessage(data.message);
        if (data.status === 'completed') {
          clearInterval(interval);
          bgRemovalIntervalRef.current = null;
          bgRemovalJobIdRef.current = null;
          const resolvedUrl = data.videoUrl?.startsWith('/api/s3/') ? `${window.location.origin}${data.videoUrl}` : data.videoUrl;

          if (bgOption === 'greenscreen') {
            // Green screen output: Replicate produced a green screen video with audio merged
            setGreenScreenUrl(resolvedUrl);
            setBgRemovalStatus('completed');
            toast({ title: 'Green screen video ready', description: 'Your video with green background is ready for rendering.' });
          } else {
            // Foreground mask output: got a luma matte for compositing
            setMaskUrl(resolvedUrl);
            // Store 30fps normalized source URL if available (prevents mask/source fps drift)
            if (data.normalizedSourceUrl) {
              const normUrl = data.normalizedSourceUrl.startsWith('/api/s3/')
                ? `${window.location.origin}${data.normalizedSourceUrl}`
                : data.normalizedSourceUrl;
              setNormalizedSourceUrl(normUrl);
            }
            setBgRemovalStatus('completed');
            // Check for mask duration mismatch
            if (data.maskDuration && data.sourceDuration && Math.abs(data.maskDuration - data.sourceDuration) > 2) {
              console.warn(`[AuditionEnhancer] Mask duration mismatch: mask=${data.maskDuration.toFixed(1)}s, source=${data.sourceDuration.toFixed(1)}s`);
              toast({ title: 'Background removed', description: `Warning: Mask covers ${data.maskDuration.toFixed(0)}s of ${data.sourceDuration.toFixed(0)}s video. Background replacement may not cover the full video.`, variant: 'destructive' });
            } else {
              toast({ title: 'Background removed', description: 'Now select a replacement background.' });
            }
          }
        } else if (data.status === 'failed' || data.status === 'expired') {
          clearInterval(interval);
          bgRemovalIntervalRef.current = null;
          bgRemovalJobIdRef.current = null;
          setBgRemovalStatus('failed');
          toast({ title: 'Background removal failed', description: data.error || 'Processing failed.', variant: 'destructive' });
        }
      } catch { /* keep polling */ }
    }, 5000);
    bgRemovalIntervalRef.current = interval;
    setTimeout(() => { clearInterval(interval); bgRemovalIntervalRef.current = null; }, 3600000);
  };

  const handleBgRemoveAndReplace = () => {
    requireCreditConfirmation(CREDIT_OPERATIONS.BACKGROUND_REMOVAL, 'Background Removal', () => startBgRemoval());
  };

  const handleBgFileUpload = async (file: File) => {
    setBgUploadingFile(true);
    try {
      const res = await fetch('/get-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileType: file.type }),
      });
      const { uploadUrl, downloadUrl } = await res.json();
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setBgReplacementUrl(downloadUrl);
      setBgReplacementType(file.type.startsWith('video/') ? 'video' : 'image');
      toast({ title: 'Background uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setBgUploadingFile(false);
    }
  };

  const bgFileDropzone = useDropzone({
    onDrop: (files) => files[0] && handleBgFileUpload(files[0]),
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'], 'video/*': ['.mp4', '.mov', '.webm'] },
    maxFiles: 1,
    disabled: bgUploadingFile,
  });

  // ===== STEP 6: RENDER =====
  const getEffectiveDuration = () => {
    if (mergedDuration > 0) return mergedDuration;
    if (clips.length === 1) return getTrimmedDuration(clips[0]);
    return clips.reduce((sum, c) => sum + getTrimmedDuration(c), 0);
  };

  const buildShotstackJson = (overrides?: { sourceUrl?: string; maskUrl?: string; greenScreenUrl?: string; normalizedSourceUrl?: string }): any => {
    const sourceVideoUrl = overrides?.sourceUrl || getSourceVideoUrl();
    // Use 30fps normalized source when compositing with mask to prevent fps drift
    const effectiveMaskUrl = overrides?.maskUrl ?? maskUrl;
    const effectiveGreenScreenUrl = overrides?.greenScreenUrl ?? greenScreenUrl;
    const effectiveNormalizedSourceUrl = overrides?.normalizedSourceUrl ?? normalizedSourceUrl;
    const compositeSourceUrl = effectiveNormalizedSourceUrl || sourceVideoUrl;
    const effectiveDuration = getEffectiveDuration();
    const tracks: any[] = [];
    // Only apply Shotstack filters (not FFmpeg presets which are already baked into the video)
    const isShotstackFilter = videoFilter !== 'none' && !FFMPEG_PRESET_KEYS.has(videoFilter);
    const activeFilter = isShotstackFilter ? videoFilter : undefined;
    const videoStart = 0;

    if (bgOption === 'remove' && effectiveMaskUrl && bgReplacementUrl) {
      // Path A: With background replacement
      // Track 0 (front): [luma mask, background] - mask cuts out person shape from bg
      // Track 1 (back): [foreground video] - person shows through
      // Both mask and source must be 30fps (backend normalizes source before Replicate)
      const foregroundClip: any = { asset: { type: 'video', src: compositeSourceUrl, volume: 1 }, start: videoStart, length: effectiveDuration };
      if (activeFilter) foregroundClip.filter = activeFilter;
      const maskClip: any = { asset: { type: 'luma', src: effectiveMaskUrl }, start: videoStart, length: effectiveDuration };

      // Background clip(s) - handles presets, video looping, and images
      const bgClips: any[] = [];
      if (bgReplacementType === 'preset' && bgReplacementUrl.startsWith('solid:')) {
        // Solid color backgrounds use image assets (not HTML - HTML breaks luma compositing)
        const solidImageMap: Record<string, string> = {
          '#808080': '/stock-backgrounds/solid-gray.png',
          '#ffffff': '/stock-backgrounds/solid-white.png',
          '#0044cc': '/stock-backgrounds/solid-blue.png',
        };
        const color = bgReplacementUrl.replace('solid:', '');
        const imagePath = solidImageMap[color] || solidImageMap['#808080'];
        bgClips.push({
          asset: { type: 'image', src: `${window.location.origin}${imagePath}` },
          start: videoStart, length: effectiveDuration, fit: 'cover',
        });
      } else {
        const needsLoop = bgReplacementType === 'video' && bgVideoLoop && bgVideoDuration && bgVideoDuration < effectiveDuration;
        if (needsLoop && bgVideoDuration) {
          let remaining = effectiveDuration;
          let offset = 0;
          while (remaining > 0) {
            const clipLen = Math.min(bgVideoDuration, remaining);
            bgClips.push({ asset: { type: 'video', src: bgReplacementUrl, volume: 0 }, start: videoStart + offset, length: clipLen, fit: 'cover' });
            offset += clipLen;
            remaining -= clipLen;
          }
        } else {
          const bgClip: any = { asset: { type: bgReplacementType === 'video' ? 'video' : 'image', src: bgReplacementUrl }, start: videoStart, length: effectiveDuration, fit: 'cover' };
          if (bgReplacementType === 'video') bgClip.asset.volume = 0;
          bgClips.push(bgClip);
        }
      }

      tracks.push({ clips: [maskClip, ...bgClips] });
      tracks.push({ clips: [foregroundClip] });
    } else if (bgOption === 'greenscreen' && effectiveGreenScreenUrl) {
      // Path C: Green screen - use Replicate's green-screen output directly (has audio merged)
      const videoClip: any = { asset: { type: 'video', src: effectiveGreenScreenUrl, volume: 1 }, start: videoStart, length: effectiveDuration };
      if (activeFilter) videoClip.filter = activeFilter;
      tracks.push({ clips: [videoClip] });
    } else if (bgOption === 'greenscreen' && effectiveMaskUrl) {
      // Path C fallback: User switched from "Remove" to "Keep as Green Screen" after mask was created
      // Composite luma matte with solid green background via Shotstack
      const foregroundClip: any = { asset: { type: 'video', src: compositeSourceUrl, volume: 1 }, start: videoStart, length: effectiveDuration };
      if (activeFilter) foregroundClip.filter = activeFilter;
      const maskClip: any = { asset: { type: 'luma', src: effectiveMaskUrl }, start: videoStart, length: effectiveDuration };
      const greenBgClip: any = {
        asset: { type: 'image', src: `${window.location.origin}/stock-backgrounds/solid-green.png` },
        start: videoStart, length: effectiveDuration, fit: 'cover',
      };
      tracks.push({ clips: [maskClip, greenBgClip] });
      tracks.push({ clips: [foregroundClip] });
    } else {
      // Path B: Without background removal
      const videoClip: any = { asset: { type: 'video', src: sourceVideoUrl, volume: 1 }, start: videoStart, length: effectiveDuration };
      if (activeFilter) videoClip.filter = activeFilter;
      tracks.push({ clips: [videoClip] });
    }

    const timeline: any = { tracks };
    const json = { timeline, output: getShotstackOutputConfig() };
    console.log('[AuditionEnhancer] Shotstack JSON:', JSON.stringify(json, null, 2));
    console.log('[AuditionEnhancer] sourceVideoUrl:', sourceVideoUrl);
    console.log('[AuditionEnhancer] maskUrl:', maskUrl);
    console.log('[AuditionEnhancer] bgReplacementUrl:', bgReplacementUrl);
    return json;
  };

  // Server-side render-job tracking so tab-close/reload can recover progress
  const createRenderJob = async (clipCount: number): Promise<string | null> => {
    const authToken = getAuthToken();
    if (!authToken) return null;
    try {
      const res = await fetch('/api/render-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ currentStage: 'starting', clipCount }),
      });
      if (!res.ok) return null;
      const { jobId } = await res.json();
      return jobId || null;
    } catch { return null; }
  };

  const patchRenderJob = (patch: Record<string, any>) => {
    const jobId = renderJobIdRef.current;
    const authToken = getAuthToken();
    if (!jobId || !authToken) return;
    fetch(`/api/render-jobs/${jobId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
      body: JSON.stringify(patch),
    }).catch(() => {});
  };

  const startRender = async () => {
    // Paid-tool gate (backstop for the button gate above): no tool access -> the
    // upgrade prompt instead of a failed /api/split-video (403 → "Render failed").
    if (!subscriptionLoading && !canUseTools) { setShowUpgradeModal(true); return; }
    // Full pipeline: trim → merge → BG removal → Shotstack → color grade → audio enhance
    const needsShotstack = !canSkipShotstack();
    const creditOp = needsShotstack ? CREDIT_OPERATIONS.SHOTSTACK_RENDER : null;

    const runPipeline = async () => {
      setRenderStatus('rendering');
      setRenderProgress(0);
      setRenderError('');
      const authToken = getAuthToken();
      const authHeaders = { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) };

      // Create server-side render job for recovery
      const jobId = await createRenderJob(clips.length);
      renderJobIdRef.current = jobId;

      try {
        // Local map to track trimmed keys (avoids stale React state closure)
        const trimResults: Record<string, { key: string; url: string }> = {};

        // ---- STEP 1: TRIM clips that need it ----
        const clipsNeedingTrim = clips.filter(c => !c.trimmedS3Key && c.s3Key);
        if (clipsNeedingTrim.length > 0) {
          setRenderStatus('trimming');
          patchRenderJob({ currentStage: 'trimming', progress: 0 });
          for (let i = 0; i < clipsNeedingTrim.length; i++) {
            const clip = clipsNeedingTrim[i];
            const startSec = parseTimeToSeconds(clip.trimStart);
            const endSec = parseTimeToSeconds(clip.trimEnd);
            setRenderProgress(Math.round((i / clipsNeedingTrim.length) * 15));
            const res = await fetch('/api/split-video', {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({
                videoKey: clip.s3Key,
                splitPoints: [{ time: endSec, label: clip.name }],
                trimStart: startSec,
                trimOnly: true, // skip the wasted "Final Segment" leftover past the trim point
                async: true,    // avoid ALB idle timeout on larger files
              }),
            });
            if (!res.ok) throw new Error(`Trim failed for ${clip.name}`);
            const trimData = await res.json();

            let segments: any[] = [];
            if (trimData.async && trimData.jobId) {
              // Trim phase is 0-15% of overall render; each clip gets an equal slice
              const clipSliceStart = Math.round((i / clipsNeedingTrim.length) * 15);
              const clipSliceEnd = Math.round(((i + 1) / clipsNeedingTrim.length) * 15);
              let trimDone = false;
              for (let attempt = 0; attempt < 400 && !trimDone; attempt++) {
                await new Promise(r => setTimeout(r, 3000));
                const statusRes = await fetch(`/api/split-video/status/${trimData.jobId}`);
                const statusData = await statusRes.json();
                if (typeof statusData.progress === 'number') {
                  const within = Math.max(0, Math.min(1, statusData.progress / 100));
                  setRenderProgress(clipSliceStart + Math.round((clipSliceEnd - clipSliceStart) * within));
                }
                if (statusData.status === 'completed' && statusData.segments) {
                  segments = statusData.segments;
                  trimDone = true;
                } else if (statusData.status === 'failed') {
                  throw new Error(statusData.error || `Trim failed for ${clip.name}`);
                }
              }
              if (!trimDone) throw new Error(`Trim timed out for ${clip.name}`);
            } else {
              segments = trimData.segments || [];
            }

            if (segments[0]) {
              const seg = segments[0];
              const trimKey = seg.segmentKey || seg.s3Key || seg.key;
              trimResults[clip.id] = { key: trimKey, url: seg.downloadUrl };
              setClips(prev => prev.map(c => c.id === clip.id ? { ...c, trimmedS3Key: trimKey, trimmedS3Url: seg.downloadUrl, status: 'trimmed' as const } : c));
            }
          }
        }
        setRenderProgress(15);

        // ---- STEP 2: MERGE clips ----
        // Use local trimResults to get correct keys (React state may be stale)
        const videoKeys = clips.map(c => {
          const trimmed = trimResults[c.id];
          return trimmed ? trimmed.key : (c.trimmedS3Key || c.s3Key);
        });

        // Track merge result locally (React state is stale in this closure)
        let pipelineKey = '';
        let pipelineUrl = '';

        if (clips.length === 1) {
          const clip = clips[0];
          const trimmed = trimResults[clip.id];
          pipelineKey = trimmed ? trimmed.key : (clip.trimmedS3Key || clip.s3Key);
          pipelineUrl = trimmed ? trimmed.url : (clip.trimmedS3Url || clip.s3Url);
        } else {
          setRenderStatus('merging');
          const mergeRes = await fetch('/api/concatenate', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ videoKeys, targetWidth: clips[0]?.width || undefined, targetHeight: clips[0]?.height || undefined }),
          });
          if (!mergeRes.ok) {
            const err = await mergeRes.json().catch(() => ({}));
            throw new Error(err.error || 'Merge failed');
          }
          const mergeData = await mergeRes.json();

          // Concat is now async — poll for completion
          if (mergeData.async && mergeData.jobId) {
            let mergeDone = false;
            for (let attempt = 0; attempt < 360 && !mergeDone; attempt++) {
              await new Promise(r => setTimeout(r, 3000));
              const statusRes = await fetch(`/api/concatenate/status/${mergeData.jobId}`);
              const statusData = await statusRes.json();
              if (statusData.progress) setRenderProgress(15 + Math.round((statusData.progress / 100) * 10));
              if (statusData.status === 'completed' && statusData.downloadUrl) {
                mergeDone = true;
                pipelineKey = statusData.concatenatedKey;
                pipelineUrl = statusData.downloadUrl;
              } else if (statusData.status === 'failed') {
                throw new Error(statusData.error || 'Merge failed');
              }
            }
            if (!mergeDone) throw new Error('Merge timed out');
          } else {
            // Legacy sync response fallback
            pipelineKey = mergeData.concatenatedKey;
            pipelineUrl = mergeData.downloadUrl;
          }
        }
        // Update state for other components that may need it
        setMergedS3Key(pipelineKey);
        setMergedS3Url(pipelineUrl);
        setRenderProgress(25);
        patchRenderJob({ currentStage: 'merge_complete', progress: 25, stages: { merge: { key: pipelineKey, url: pipelineUrl } } });

        // ---- STEP 3: COLOR GRADE (if FFmpeg preset or custom adjustments) ----
        // Apply before BG removal so the grade only affects the subject, not the new background
        const hasCustomAdj = Object.values(colorAdjustments).some(v => v !== 0);
        if (FFMPEG_PRESET_KEYS.has(videoFilter) || hasCustomAdj) {
          setRenderStatus('color_grading');
          patchRenderJob({ currentStage: 'color_grading' });
          try {
            const filterRes = await fetch('/api/filter/apply', {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({
                videoUrl: pipelineUrl,
                filter: FFMPEG_PRESET_KEYS.has(videoFilter) ? videoFilter : undefined,
                colorAdjustments: hasCustomAdj ? colorAdjustments : undefined,
              }),
            });
            if (filterRes.ok) {
              const filterData = await filterRes.json();

              // Filter is now async — poll for completion (up to 20 min)
              if (filterData.async && filterData.jobId) {
                let filterDone = false;
                for (let attempt = 0; attempt < 400 && !filterDone; attempt++) {
                  await new Promise(r => setTimeout(r, 3000));
                  const statusRes = await fetch(`/api/filter/apply/status/${filterData.jobId}`);
                  const statusData = await statusRes.json();
                  if (statusData.progress) setRenderProgress(25 + Math.round((statusData.progress / 100) * 10));
                  if (statusData.status === 'completed' && statusData.downloadUrl) {
                    filterDone = true;
                    pipelineKey = statusData.key || pipelineKey;
                    pipelineUrl = statusData.downloadUrl;
                  } else if (statusData.status === 'failed') {
                    throw new Error(statusData.error || 'Color grade failed');
                  }
                }
                if (!filterDone) throw new Error('Color grade timed out');
              } else {
                // Legacy sync response fallback
                pipelineKey = filterData.key || pipelineKey;
                pipelineUrl = filterData.downloadUrl;
              }
              setFilteredVideoKey(pipelineKey);
              setFilteredVideoUrl(pipelineUrl);
              patchRenderJob({ stages: { colorGrade: { key: pipelineKey, url: pipelineUrl } } });
            } else {
              console.error('[AuditionEnhancer] Color grade failed, continuing without it');
              toast({ title: 'Color grade failed', description: 'Continuing without color grade.', variant: 'destructive' });
            }
          } catch (err: any) {
            console.error('[AuditionEnhancer] Color grade error:', err);
            toast({ title: 'Color grade failed', description: 'Continuing without color grade.', variant: 'destructive' });
          }
        }
        setRenderProgress(35);

        // ---- STEP 4: BACKGROUND REMOVAL (if selected) ----
        // Track BG results locally (React state is stale in this closure)
        let localMaskUrl = '';
        let localGreenScreenUrl = '';
        let localNormalizedSourceUrl = '';

        if (bgOption !== 'skip') {
          // Reuse existing mask/green-screen from previous render if available
          // (mask is invalidated when source video changes via resetFromMerge)
          const existingMask = bgOption === 'greenscreen' ? greenScreenUrl : maskUrl;

          if (existingMask) {
            console.log('[AuditionEnhancer] Reusing existing BG removal result');
            if (bgOption === 'greenscreen') {
              localGreenScreenUrl = greenScreenUrl;
            } else {
              localMaskUrl = maskUrl;
              localNormalizedSourceUrl = normalizedSourceUrl;
            }
          } else {
            setRenderStatus('bg_removing');
            patchRenderJob({ currentStage: 'bg_removing' });
            const outputType = bgOption === 'greenscreen' ? 'green-screen' : 'foreground-mask';

            const bgRes = await fetch('/api/remove-video-background', {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({ videoUrl: pipelineUrl, videoKey: pipelineKey, outputType }),
            });
            if (!bgRes.ok) throw new Error('Background removal failed to start');
            const { jobId: bgJobId } = await bgRes.json();

            // Poll until complete
            let bgDone = false;
            for (let attempt = 0; attempt < 720 && !bgDone; attempt++) {
              await new Promise(r => setTimeout(r, 5000));
              const statusRes = await fetch(`/api/remove-video-background/status/${bgJobId}`);
              const statusData = await statusRes.json();
              if (statusData.progress) setRenderProgress(35 + Math.round(statusData.progress * 0.2));
              if (statusData.status === 'completed') {
                bgDone = true;
                const resolvedUrl = statusData.videoUrl?.startsWith('/api/s3/') ? `${window.location.origin}${statusData.videoUrl}` : statusData.videoUrl;
                if (bgOption === 'greenscreen') {
                  localGreenScreenUrl = resolvedUrl;
                  setGreenScreenUrl(resolvedUrl);
                } else {
                  localMaskUrl = resolvedUrl;
                  setMaskUrl(resolvedUrl);
                  if (statusData.normalizedSourceUrl) {
                    const normUrl = statusData.normalizedSourceUrl.startsWith('/api/s3/') ? `${window.location.origin}${statusData.normalizedSourceUrl}` : statusData.normalizedSourceUrl;
                    localNormalizedSourceUrl = normUrl;
                    setNormalizedSourceUrl(normUrl);
                  }
                }
                setBgRemovalStatus('completed');
              } else if (statusData.status === 'failed' || statusData.status === 'expired') {
                throw new Error(statusData.error || 'Background removal failed');
              }
            }
            if (!bgDone) throw new Error('Background removal timed out');
            patchRenderJob({ stages: { bgRemoval: { maskUrl: localMaskUrl, greenScreenUrl: localGreenScreenUrl, normalizedSourceUrl: localNormalizedSourceUrl } } });
          }
        }
        setRenderProgress(60);

        // ---- STEP 5: SHOTSTACK RENDER (or skip) ----
        if (needsShotstack) {
          setRenderStatus('rendering');
          patchRenderJob({ currentStage: 'rendering', progress: 60 });
          const json = buildShotstackJson({
            sourceUrl: pipelineUrl,
            maskUrl: localMaskUrl || undefined,
            greenScreenUrl: localGreenScreenUrl || undefined,
            normalizedSourceUrl: localNormalizedSourceUrl || undefined,
          });
          const renderRes = await fetch('/api/shotstack/render', {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({ json }),
          });
          if (!renderRes.ok) {
            const err = await renderRes.json().catch(() => ({}));
            throw new Error(err.error || err.details || 'Render submission failed');
          }
          const renderData = await renderRes.json();
          patchRenderJob({ stages: { shotstack: { renderId: renderData.id } } });
          toast({ title: 'Rendering video...' });
          pollRenderStatus(renderData.id);
        } else {
          // No Shotstack needed — go straight to post-render
          if (!pipelineUrl) throw new Error('No source video found.');
          setRenderProgress(100);
          postRenderPipeline(pipelineUrl);
        }
      } catch (err: any) {
        setRenderStatus('failed');
        setRenderError(err.message);
        patchRenderJob({ status: 'failed', error: err.message });
        toast({ title: 'Processing failed', description: err.message, variant: 'destructive' });
      }
    };

    // If Shotstack render is needed, confirm credit first
    if (creditOp) {
      requireCreditConfirmation(creditOp, 'Video Render', runPipeline);
    } else {
      runPipeline();
    }
  };

  // Post-render pipeline: Shotstack done → audio enhance (if selected) → finish
  // Color grade now happens before BG removal in the main pipeline
  const postRenderPipeline = async (renderedUrl: string) => {
    if (enhanceAudio) {
      setRenderStatus('audio_enhancing');
      patchRenderJob({ currentStage: 'audio_enhancing', progress: 95 });
      startAudioEnhancement(renderedUrl);
    } else {
      finishRender(renderedUrl);
    }
  };

  const pollRenderStatus = (id: string) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`/api/shotstack/render-status/${id}`);
        const data = await res.json();
        const doneUrl = data.videoUrl || data.url;
        if (data.status === 'done' && doneUrl) {
          clearInterval(interval);
          setRenderProgress(100);
          patchRenderJob({ stages: { shotstack: { renderId: id, url: doneUrl } } });
          postRenderPipeline(doneUrl);
        } else if (data.status === 'failed') {
          clearInterval(interval);
          const errMsg = data.errorDetails?.message || data.error || data.message || 'Rendering failed.';
          console.error('[AuditionEnhancer] Render failed:', data);
          setRenderStatus('failed');
          setRenderError(errMsg);
          patchRenderJob({ status: 'failed', error: errMsg });
          toast({ title: 'Render failed', description: errMsg, variant: 'destructive' });
        } else {
          if (data.status === 'fetching') setRenderProgress(10);
          else if (data.status === 'queued') setRenderProgress(20);
          else if (data.status === 'rendering') setRenderProgress(prev => Math.min(90, prev + 5));
          else if (data.status === 'saving') setRenderProgress(95);
        }
        if (attempts >= 360) {
          clearInterval(interval);
          setRenderStatus('failed');
          patchRenderJob({ status: 'failed', error: 'Render timed out' });
          toast({ title: 'Render timed out', variant: 'destructive' });
        }
      } catch { if (attempts >= 360) { clearInterval(interval); setRenderStatus('failed'); } }
    }, 5000);
  };

  const finishRender = (url: string) => {
    setFinalVideoUrl(url);
    setRenderStatus('completed');
    setCompletedSteps(prev => new Set([...prev, 'enhance', 'audio', 'background', 'render']));
    setCurrentStep('download');
    patchRenderJob({ status: 'completed', progress: 100, currentStage: 'completed', finalVideoUrl: url });
  };

  const startAudioEnhancement = async (videoUrl: string) => {
    const modeLabels: Record<string, string> = { light: 'Enhancing', natural: 'Enhancing', strong: 'Enhancing' };
    try {
      const authToken = getAuthToken();
      const res = await fetch('/api/enhance-video-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ videoUrl, mode: audioEnhanceMode }),
      });
      if (!res.ok) throw new Error('Audio enhancement failed');
      const { jobId } = await res.json();
      toast({ title: `${modeLabels[audioEnhanceMode]} audio`, description: 'Improving audio quality...' });

      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await fetch(`/api/enhance-video-audio/status/${jobId}`);
          const statusData = await statusRes.json();
          if (statusData.status === 'completed' && statusData.videoUrl) {
            clearInterval(interval);
            finishRender(statusData.videoUrl);
            toast({ title: 'Audio enhanced', description: 'Your video is ready to download.' });
          } else if (statusData.status === 'failed' || attempts >= 240) {
            clearInterval(interval);
            finishRender(videoUrl);
            toast({ title: 'Audio enhancement failed', description: 'Using original audio.', variant: 'destructive' });
          }
        } catch { if (attempts >= 240) { clearInterval(interval); finishRender(videoUrl); } }
      }, 5000);
    } catch {
      finishRender(videoUrl);
      toast({ title: 'Audio enhancement failed', description: 'Using original audio.', variant: 'destructive' });
    }
  };

  // 10-second audio preview so users can A/B Natural vs Strong before committing.
  const previewAudioEnhancement = async () => {
    const sourceClip = clips[0];
    const sourceUrl = sourceClip?.trimmedS3Url || sourceClip?.s3Url;
    const sourceKey = sourceClip?.trimmedS3Key || sourceClip?.s3Key;
    if (!sourceUrl && !sourceKey) {
      toast({ title: 'No audio available to preview yet', variant: 'destructive' });
      return;
    }
    setAudioPreviewLoading(true);
    setAudioPreviewUrl(null);
    setAudioPreviewMode(audioEnhanceMode);
    try {
      const authToken = getAuthToken();
      const startRes = await fetch('/api/audio-enhance/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          videoUrl: sourceUrl,
          videoKey: sourceKey,
          sourceBucket: 'hey-joe-video-rotator',
          mode: audioEnhanceMode,
          startTime: 0,
        }),
      });
      if (!startRes.ok) throw new Error('Preview request failed');
      const { jobId } = await startRes.json();

      // Poll up to 3 minutes (Auphonic on a 10s clip is usually <1 min)
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const sRes = await fetch(`/api/audio-enhance/preview/status/${jobId}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        });
        if (!sRes.ok) continue;
        const sData = await sRes.json();
        if (sData.status === 'completed' && sData.audioUrl) {
          setAudioPreviewUrl(sData.audioUrl);
          done = true;
        } else if (sData.status === 'failed') {
          throw new Error(sData.error || 'Preview failed');
        }
      }
      if (!done) throw new Error('Preview timed out');
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
      setAudioPreviewMode(null);
    } finally {
      setAudioPreviewLoading(false);
    }
  };

  // Signature of the current filter selection — preview is "fresh" while this matches colorPreviewSig.
  const colorFilterSig = () => `${videoFilter}|${JSON.stringify(colorAdjustments)}`;

  // Render a real 10s preview of the current filter via FFmpeg (the CSS preview
  // only approximates eq/colorbalance). Supported for FFmpeg presets + custom
  // adjustments; Shotstack-only filters fall back to the CSS approximation.
  const previewColorFilter = async () => {
    const sourceUrl = getUnfilteredVideoUrl();
    const sourceKey = getUnfilteredVideoKey();
    if (!sourceUrl && !sourceKey) {
      toast({ title: 'No video to preview yet', variant: 'destructive' });
      return;
    }
    const hasAdj = Object.values(colorAdjustments).some(v => v !== 0);
    const isFfmpegPreset = FFMPEG_PRESET_KEYS.has(videoFilter);
    if (!isFfmpegPreset && !hasAdj) {
      toast({ title: 'Pick a color grade or adjust the sliders first', variant: 'destructive' });
      return;
    }
    const sig = colorFilterSig();
    setColorPreviewLoading(true);
    setColorPreviewUrl(null);
    try {
      const authToken = getAuthToken();
      const startRes = await fetch('/api/color-filter/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          videoUrl: sourceUrl,
          videoKey: sourceKey,
          sourceBucket: 'hey-joe-video-rotator',
          filter: isFfmpegPreset ? videoFilter : undefined,
          colorAdjustments: hasAdj ? colorAdjustments : undefined,
          startTime: 0,
        }),
      });
      if (!startRes.ok) throw new Error('Preview request failed');
      const { jobId } = await startRes.json();

      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const sRes = await fetch(`/api/color-filter/preview/status/${jobId}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        });
        if (!sRes.ok) continue;
        const sData = await sRes.json();
        if (sData.status === 'completed' && sData.videoUrl) {
          setColorPreviewUrl(sData.videoUrl);
          setColorPreviewSig(sig);
          done = true;
        } else if (sData.status === 'failed') {
          throw new Error(sData.error || 'Preview failed');
        }
      }
      if (!done) throw new Error('Preview timed out');
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setColorPreviewLoading(false);
    }
  };

  // Render a real 3s preview of the background replacement on the user's own
  // clip — Replicate matte + local composite over the chosen backdrop, so they
  // see the actual result before committing a full render. (Image backdrops only.)
  const previewBackground = async () => {
    const sourceUrl = getSourceVideoUrl();
    const sourceKey = getSourceVideoKey();
    if (!sourceUrl && !sourceKey) {
      toast({ title: 'No video to preview yet', variant: 'destructive' });
      return;
    }
    if (!bgReplacementUrl) {
      toast({ title: 'Pick a backdrop first', variant: 'destructive' });
      return;
    }
    const sig = bgReplacementUrl;
    setBgPreviewLoading(true);
    setBgPreviewUrl(null);
    try {
      const authToken = getAuthToken();
      const startRes = await fetch('/api/bg-replace/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({
          videoUrl: sourceUrl,
          videoKey: sourceKey,
          bgImageUrl: bgReplacementUrl,
          sourceBucket: 'hey-joe-video-rotator',
          startTime: 0,
          durationSec: 3,
        }),
      });
      if (!startRes.ok) throw new Error('Preview request failed');
      const { jobId } = await startRes.json();

      // Replicate matting takes longer than the local previews — poll up to ~5 min.
      let done = false;
      for (let i = 0; i < 100 && !done; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const sRes = await fetch(`/api/bg-replace/preview/status/${jobId}`, {
          headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        });
        if (!sRes.ok) continue;
        const sData = await sRes.json();
        if (sData.status === 'completed' && sData.videoUrl) {
          setBgPreviewUrl(sData.videoUrl);
          setBgPreviewSig(sig);
          done = true;
        } else if (sData.status === 'failed') {
          throw new Error(sData.error || 'Preview failed');
        }
      }
      if (!done) throw new Error('Preview timed out');
    } catch (err: any) {
      toast({ title: 'Background preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setBgPreviewLoading(false);
    }
  };

  // ===== RENDER UI =====
  // Step indicator chrome is owned by ToolShell (ToolSidebar at lg+,
  // HorizontalStepper at sm). The old centered-pill renderStepIndicator()
  // was removed at the P2.2 shell wrap; ToolShell + sidebar drawer + sm
  // horizontal stepper replaces it across all breakpoints.


  const renderUploadStep = () => {
    const handleRecordClick = () => {
      if (!getAuthToken()) { setShowLoginModal(true); return; }
      if (!subscriptionLoading && !isSubscriber) { setShowUpgradeModal(true); return; }
      setShowRecordingModal(true);
    };

    return (
      <StepCard>
        {/* Two-up: Record (primary, blue accent) + Drop (dashed). Stacks to
            1-col at sm per mobile rules. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {/* Record card */}
          <button
            type="button"
            onClick={handleRecordClick}
            className="flex flex-col justify-between text-left transition-opacity hover:opacity-95"
            style={{
              padding: '18px 20px',
              minHeight: 140,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-blue)',
              color: 'oklch(0.16 0.005 70)',
              border: 0,
              gap: 14,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  background: 'oklch(0.16 0.005 70 / 0.18)',
                }}
              >
                <Video size={18} strokeWidth={1.8} />
              </div>
              <span className="mono-label hidden sm:inline" style={{ color: 'oklch(0.16 0.005 70 / 0.7)', fontSize: 9 }}>
                ⌘R
              </span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2 }}>Record with Teleprompter</div>
              <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.4 }}>
                Live camera, scrolling sides, scene partner reads opposite.
              </div>
            </div>
          </button>

          {/* Drop card */}
          <div
            {...getRootProps()}
            className="flex flex-col justify-between cursor-pointer transition-colors"
            style={{
              padding: '18px 20px',
              minHeight: 140,
              borderRadius: 'var(--hj-r-md)',
              border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
              background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
              gap: 14,
            }}
          >
            <input {...getInputProps()} />
            <div className="flex items-center justify-between gap-3">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-3)',
                  color: 'var(--hj-ink-2)',
                }}
              >
                <Upload size={18} strokeWidth={1.8} />
              </div>
              <span className="mono-label hidden sm:inline" style={{ fontSize: 9 }}>
                ⌘U
              </span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--hj-ink)', marginBottom: 2 }}>
                Drop or browse clips
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.4 }}>
                MP4 · MOV · WebM · MKV — multiple files welcome.
              </div>
            </div>
          </div>
        </div>

        {/* Clip list */}
        {clips.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div className="mono-label" style={{ marginBottom: 10 }}>
              Uploaded clips · <span style={{ color: 'var(--hj-ink-2)' }}>{clips.length}</span>
            </div>
            <div className="flex flex-col" style={{ gap: 8 }}>
              {clips.map((clip) => {
                const isUploading = clip.status === 'uploading';
                const isConverting = clip.status === 'converting';
                const isReady = clip.status === 'uploaded' || clip.status === 'trimmed';
                const isError = clip.status === 'error';
                return (
                  <div
                    key={clip.id}
                    className="flex items-center"
                    style={{
                      gap: 14,
                      padding: '10px 12px',
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-2)',
                      border: '1px solid var(--hj-hairline-soft)',
                    }}
                  >
                    <div
                      className="flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                      style={{
                        width: 76,
                        height: 44,
                        borderRadius: 4,
                        background: 'oklch(0.12 0.005 70)',
                        color: 'var(--hj-ink-4)',
                      }}
                    >
                      <Video size={16} strokeWidth={1.5} />
                      {clip.duration > 0 && (
                        <span
                          className="tabular"
                          style={{
                            position: 'absolute',
                            right: 3,
                            bottom: 3,
                            padding: '1px 5px',
                            borderRadius: 2,
                            background: 'oklch(0 0 0 / 0.6)',
                            color: 'var(--hj-ink)',
                            fontFamily: 'var(--hj-font-mono)',
                            fontSize: 9,
                          }}
                        >
                          {formatTime(clip.duration)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="truncate"
                        style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}
                      >
                        {clip.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--hj-ink-3)', marginTop: 2 }}>
                        {isUploading
                          ? `Uploading… ${clip.uploadProgress ?? 0}%`
                          : isConverting
                            ? 'Converting MOV → MP4'
                            : isError
                              ? 'Upload error'
                              : clip.width && clip.height
                                ? `Uploaded · ${clip.width}×${clip.height}`
                                : 'Uploaded'}
                      </div>
                      {isUploading && (
                        <Progress value={clip.uploadProgress ?? 0} className="h-1 mt-1.5" />
                      )}
                    </div>
                    {isUploading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
                    {isConverting && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
                    {isReady && <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-yellow-ink)' }} />}
                    {isError && <AlertCircle className="w-4 h-4 text-destructive" />}
                    <button
                      type="button"
                      onClick={() => removeClip(clip.id)}
                      aria-label="Remove clip"
                      className="flex items-center justify-center"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: 'transparent',
                        border: 0,
                        color: 'var(--hj-ink-3)',
                        cursor: 'pointer',
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* In-card footer — md+ only; at sm the MobileActionBar handles
                primary advance. "Add more" stays here at all sizes since it's
                tool-specific (the toolbar/bar only knows Back/Next). */}
            <div className="flex justify-end items-center gap-2.5" style={{ marginTop: 14 }}>
              <button
                type="button"
                {...getRootProps()}
                className="inline-flex items-center gap-1.5 cursor-pointer"
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid var(--hj-hairline)',
                  background: 'transparent',
                  color: 'var(--hj-ink-2)',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  minHeight: 44,
                }}
              >
                <input {...getInputProps()} />
                <Plus className="w-3.5 h-3.5" /> Add more
              </button>
              <button
                type="button"
                onClick={advanceStep}
                disabled={!allClipsUploaded}
                className="hidden md:inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '8px 16px',
                  borderRadius: 999,
                  background: 'var(--hj-blue)',
                  border: '1px solid var(--hj-blue)',
                  color: 'oklch(0.18 0.005 70)',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: allClipsUploaded ? 'pointer' : 'not-allowed',
                  opacity: allClipsUploaded ? 1 : 0.5,
                }}
              >
                Continue to Trim
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderTrimStep = () => {
    const clip = clips[trimClipIndex];
    if (!clip) return null;
    const inSec = parseTimeToSeconds(clip.trimStart);
    const outSec = parseTimeToSeconds(clip.trimEnd);
    const handlesMoved = inSec > 0.5 || outSec < clip.duration - 0.5;
    const selectedDur = Math.max(0, outSec - inSec);
    const isTrimming = clip.status === 'trimming';
    const isFirstClip = trimClipIndex === 0;
    const isLastClip = trimClipIndex === clips.length - 1;

    const handleTrimSliderChange = (values: number[]) => {
      const oldIn = inSec;
      const oldOut = outSec;
      updateClipTrim(clip.id, 'trimStart', formatTime(values[0]));
      updateClipTrim(clip.id, 'trimEnd', formatTime(values[1]));
      if (Math.abs(values[0] - oldIn) > 0.05) seekTo(values[0]);
      else if (Math.abs(values[1] - oldOut) > 0.05) seekTo(values[1]);
    };

    const handleResetTrim = () => {
      updateClipTrim(clip.id, 'trimStart', formatTime(0));
      updateClipTrim(clip.id, 'trimEnd', formatTime(clip.duration));
      seekTo(0);
    };

    const handleSkip = () => {
      skipTrimForClip(clip.id);
      advanceTrimClip();
    };

    const handleTrimAction = async () => {
      if (handlesMoved) {
        const success = await applyTrimToClip(clip.id);
        if (!success) return;
      } else {
        skipTrimForClip(clip.id);
      }
      advanceTrimClip();
    };

    const isPortrait = clip.width && clip.height && clip.height > clip.width;
    const isSquare = clip.width && clip.height && Math.abs(clip.width / clip.height - 1) < 0.05;

    return (
      <StepCard>
        {/* Local title bar — clip name + pagination, no in-card hero. */}
        <div className="flex items-center justify-between gap-3" style={{ marginBottom: 14 }}>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="mono-label">Clip</span>
            <span
              className="truncate"
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--hj-ink)',
                fontFamily: 'var(--hj-font-mono)',
              }}
            >
              {clip.name}
            </span>
          </div>
          {clips.length > 1 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => isFirstClip ? null : setTrimClipIndex(trimClipIndex - 1)}
                disabled={isFirstClip}
                aria-label="Previous clip"
                className="inline-flex items-center gap-1 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '6px 10px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-3)',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  cursor: isFirstClip ? 'not-allowed' : 'pointer',
                  opacity: isFirstClip ? 0.4 : 1,
                }}
              >
                ← Prev
              </button>
              <span className="mono-label tabular" style={{ color: 'var(--hj-ink-3)' }}>
                {String(trimClipIndex + 1).padStart(2, '0')} / {String(clips.length).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => isLastClip ? null : setTrimClipIndex(trimClipIndex + 1)}
                disabled={isLastClip}
                aria-label="Next clip"
                className="inline-flex items-center gap-1 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '6px 10px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-3)',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  cursor: isLastClip ? 'not-allowed' : 'pointer',
                  opacity: isLastClip ? 0.4 : 1,
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Video preview — capped at 320px tall so the timeline + footer stay
            above the fold on shorter viewports. */}
        <div
          className={`mx-auto overflow-hidden ${isSquare ? 'aspect-square' : isPortrait ? 'aspect-[9/16]' : 'aspect-video'}`}
          style={{
            background: 'oklch(0.1 0.005 70)',
            borderRadius: 'var(--hj-r-md)',
            border: '1px solid var(--hj-hairline)',
            marginBottom: 16,
            maxHeight: 320,
            width: '100%',
          }}
        >
          {videoPreviewError ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center" style={{ color: 'var(--hj-ink-2)' }}>
              <Video className="w-10 h-10 mb-3 opacity-50" />
              <p className="text-sm font-medium">Preview not available for this format</p>
              <p className="text-xs mt-1 opacity-70">You can still set trim points — the video will process correctly.</p>
            </div>
          ) : (
            <video ref={videoRef} className="w-full h-full object-contain" controls={false} preload="auto" />
          )}
        </div>

        {/* Playback control + timeline */}
        {!videoPreviewError && clip.duration > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
              <button
                type="button"
                onClick={togglePlayPause}
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: 'var(--hj-surface-2)',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  cursor: 'pointer',
                }}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <span
                className="tabular"
                style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 12, color: 'var(--hj-ink-3)' }}
              >
                {formatTime(currentTime)} / {formatTime(clip.duration)}
              </span>
            </div>
            <HJTrimTimeline
              duration={clip.duration}
              currentTime={currentTime}
              trimStart={inSec}
              trimEnd={outSec}
              onSeek={seekTo}
              onTrimChange={(start, end) => handleTrimSliderChange([start, end])}
              isPlaying={isPlaying}
            />
            {/* Readout */}
            <div
              className="flex items-center justify-between"
              style={{ marginTop: 10, fontSize: 12 }}
            >
              <span style={{ color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                In · <span style={{ color: 'var(--hj-ink-2)' }}>{formatTime(inSec)}</span>
              </span>
              <span
                className="inline-flex items-center gap-1.5"
                style={{
                  padding: '3px 10px',
                  borderRadius: 999,
                  background: 'var(--hj-blue-tint)',
                  color: 'var(--hj-yellow-ink)',
                  fontFamily: 'var(--hj-font-mono)',
                  fontWeight: 600,
                }}
              >
                {formatTime(selectedDur)} selected
              </span>
              <span style={{ color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                Out · <span style={{ color: 'var(--hj-ink-2)' }}>{formatTime(outSec)}</span>
              </span>
            </div>
          </div>
        )}

        {/* Footer actions — md+ only. sm has the MobileActionBar; Reset stays
            here at all sizes since the bar only knows Back/Next. */}
        <div
          className="flex justify-end items-center gap-2.5"
          style={{
            paddingTop: 12,
            borderTop: '1px solid var(--hj-hairline-soft)',
          }}
        >
          <button
            type="button"
            onClick={handleResetTrim}
            disabled={!handlesMoved || isTrimming}
            className="inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: handlesMoved && !isTrimming ? 'pointer' : 'not-allowed',
              opacity: handlesMoved && !isTrimming ? 1 : 0.5,
              minHeight: 44,
            }}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={isTrimming}
            className="hidden md:inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--hj-hairline)',
              color: 'var(--hj-ink-2)',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: isTrimming ? 'not-allowed' : 'pointer',
              opacity: isTrimming ? 0.5 : 1,
            }}
          >
            Skip trim
          </button>
          <button
            type="button"
            onClick={handleTrimAction}
            disabled={isTrimming}
            className="hidden md:inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: isTrimming ? 'not-allowed' : 'pointer',
              opacity: isTrimming ? 0.6 : 1,
            }}
          >
            {isTrimming ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Trimming…</>
            ) : handlesMoved ? (
              <><Scissors className="w-3.5 h-3.5" /> Trim & continue <ArrowRight className="w-3.5 h-3.5" /></>
            ) : (
              <>Use full clip <ArrowRight className="w-3.5 h-3.5" /></>
            )}
          </button>
        </div>
      </StepCard>
    );
  };

  const renderMergeStep = () => {
    const totalDur = clips.reduce((sum, c) => sum + getTrimmedDuration(c), 0);
    return (
      <StepCard>
        <StepHeader
          kicker={`Step 03 · ${clips.length === 1 ? 'Single clip' : 'Arrange & merge'}`}
          title={clips.length === 1 ? 'One take — passed through.' : 'Line up the takes.'}
          subtitle={
            clips.length === 1
              ? 'You only uploaded one clip. It will be used as-is — continue to apply your filter.'
              : 'Drag or use the arrows to reorder. Joe will stitch them into one continuous tape.'
          }
        />

        <div className="flex flex-col gap-2" style={{ marginBottom: 16 }}>
          {clips.map((clip, i) => {
            const isOpen = previewClipId === clip.id;
            const dur = getTrimmedDuration(clip);
            return (
              <div
                key={clip.id}
                className="overflow-hidden"
                style={{
                  borderRadius: 'var(--hj-r-sm)',
                  background: 'var(--hj-surface-2)',
                  border: '1px solid var(--hj-hairline-soft)',
                }}
              >
                <div className="flex items-center gap-3.5" style={{ padding: '12px 14px' }}>
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center rounded-full flex-shrink-0 tabular"
                    style={{
                      width: 28,
                      height: 28,
                      background: 'var(--hj-surface-3)',
                      color: 'var(--hj-ink-2)',
                      fontFamily: 'var(--hj-font-mono)',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPreviewClipId(isOpen ? null : clip.id)}
                    aria-label={isOpen ? 'Hide preview' : 'Show preview'}
                    className="relative flex-shrink-0 overflow-hidden group cursor-pointer"
                    style={{
                      width: 76,
                      height: 44,
                      borderRadius: 4,
                      background: 'oklch(0.1 0.005 70)',
                      border: 0,
                      padding: 0,
                    }}
                  >
                    <video
                      src={clip.trimmedS3Url || clip.objectUrl}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-4 h-4 text-white" />
                    </div>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div
                      className="truncate"
                      style={{ fontSize: 13.5, color: 'var(--hj-ink)', fontWeight: 500 }}
                    >
                      {clip.name}
                    </div>
                    <div
                      className="tabular"
                      style={{ fontSize: 11, color: 'var(--hj-ink-3)', marginTop: 2 }}
                    >
                      Trimmed · {formatTime(dur)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewClipId(isOpen ? null : clip.id)}
                    className="mono-label"
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'transparent',
                      border: '1px solid var(--hj-hairline)',
                      color: 'var(--hj-ink-3)',
                      cursor: 'pointer',
                      minHeight: 32,
                    }}
                  >
                    {isOpen ? 'Hide' : 'Preview'}
                  </button>
                  {clips.length > 1 && (
                    <div className="flex flex-col" style={{ gap: 2 }}>
                      <button
                        type="button"
                        onClick={() => moveClip(i, 'up')}
                        disabled={i === 0}
                        aria-label="Move up"
                        className="inline-flex items-center justify-center disabled:cursor-not-allowed"
                        style={{
                          width: 28,
                          height: 22,
                          background: 'transparent',
                          border: '1px solid var(--hj-hairline)',
                          borderRadius: 4,
                          color: 'var(--hj-ink-3)',
                          cursor: i === 0 ? 'not-allowed' : 'pointer',
                          opacity: i === 0 ? 0.3 : 1,
                        }}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveClip(i, 'down')}
                        disabled={i === clips.length - 1}
                        aria-label="Move down"
                        className="inline-flex items-center justify-center disabled:cursor-not-allowed"
                        style={{
                          width: 28,
                          height: 22,
                          background: 'transparent',
                          border: '1px solid var(--hj-hairline)',
                          borderRadius: 4,
                          color: 'var(--hj-ink-3)',
                          cursor: i === clips.length - 1 ? 'not-allowed' : 'pointer',
                          opacity: i === clips.length - 1 ? 0.3 : 1,
                        }}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {isOpen && (
                  <div style={{ padding: '0 14px 14px' }}>
                    <div
                      className="aspect-video overflow-hidden"
                      style={{
                        background: 'oklch(0.1 0.005 70)',
                        borderRadius: 'var(--hj-r-sm)',
                      }}
                    >
                      <video
                        src={clip.trimmedS3Url || clip.objectUrl}
                        className="w-full h-full object-contain"
                        controls
                        preload="auto"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {mergeError && (
          <div
            className="flex items-start gap-3"
            style={{
              padding: 12,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
              marginBottom: 12,
            }}
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--destructive))' }} />
            <div className="flex-1">
              <p className="text-sm" style={{ color: 'hsl(var(--destructive))', fontWeight: 600 }}>
                Merge failed
              </p>
              <p style={{ fontSize: 12, color: 'hsl(var(--destructive) / 0.85)', marginTop: 2 }}>
                {mergeError}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMergeError('')}
              className="flex items-center justify-center"
              style={{
                width: 24,
                height: 24,
                borderRadius: 4,
                background: 'transparent',
                border: 0,
                color: 'hsl(var(--destructive))',
                cursor: 'pointer',
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer: total duration + in-card primary action (md+ only) */}
        <div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center"
          style={{
            paddingTop: 12,
            borderTop: '1px solid var(--hj-hairline-soft)',
            gap: 12,
          }}
        >
          <div className="flex items-center gap-2 mono-label">
            <span style={{ color: 'var(--hj-ink-4)' }}>Total</span>
            <span className="tabular" style={{ color: 'var(--hj-ink-2)', fontSize: 11 }}>
              {formatTime(totalDur)}
            </span>
          </div>
          <button
            type="button"
            onClick={mergeClips}
            disabled={merging}
            className="hidden md:inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: merging ? 'not-allowed' : 'pointer',
              opacity: merging ? 0.6 : 1,
            }}
          >
            {merging ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Merging…</>
            ) : clips.length === 1 ? (
              <>Continue <ArrowRight className="w-3.5 h-3.5" /></>
            ) : (
              <><Layers className="w-3.5 h-3.5" /> Merge & continue</>
            )}
          </button>
        </div>
      </StepCard>
    );
  };

  const renderBackgroundStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 06 · Background"
        title="Drop the room."
        subtitle="Most self-tapes keep the room. Or swap in a neutral backdrop, upload your own, or export with a green-screen matte."
      />

      {bgRemovalStatus === 'idle' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" style={{ marginBottom: 22 }}>
          {([
            { k: 'skip',        t: 'Keep it',       sub: 'Use the original room',     Icon: ArrowRight },
            { k: 'remove',      t: 'Replace',       sub: 'Drop in a clean backdrop', Icon: Monitor },
            { k: 'greenscreen', t: 'Green-screen',  sub: 'Export with alpha matte',   Icon: Layers   },
          ] as const).map((o) => {
            const active = bgOption === o.k;
            return (
              <button
                key={o.k}
                type="button"
                onClick={() => setBgOption(o.k)}
                className="flex flex-col items-start gap-1.5 text-left transition-colors"
                style={{
                  padding: 14,
                  borderRadius: 'var(--hj-r-sm)',
                  background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                  border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                  color: 'var(--hj-ink)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  minHeight: 92,
                }}
              >
                <o.Icon className="w-5 h-5" style={{ color: active ? 'var(--hj-blue)' : 'var(--hj-ink-3)' }} />
                <div style={{ fontSize: 14, fontWeight: 600 }}>{o.t}</div>
                <div style={{ fontSize: 12, color: 'var(--hj-ink-2)' }}>{o.sub}</div>
              </button>
            );
          })}
        </div>
      )}

      {bgOption === 'remove' && bgRemovalStatus === 'idle' && (
        <div className="flex flex-col" style={{ gap: 18 }}>
          {/* Studio backdrops — graded (soft vignette + tone) so they read as a
              real backdrop instead of flat paper. Two takes per color: Soft
              (neutral) and Warm/Deep. Composited as image backgrounds. */}
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>Studio backdrops</div>
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { file: 'studio-gray-soft.png',  label: 'Gray · Soft' },
                { file: 'studio-gray-warm.png',  label: 'Gray · Warm' },
                { file: 'studio-white-soft.png', label: 'White · Soft' },
                { file: 'studio-white-warm.png', label: 'White · Warm' },
                { file: 'studio-blue-soft.png',  label: 'Blue · Soft' },
                { file: 'studio-blue-deep.png',  label: 'Blue · Deep' },
              ].map((bd) => {
                const src = `/stock-backgrounds/${bd.file}`;
                const url = `${window.location.origin}${src}`;
                const active = bgReplacementUrl === url;
                return (
                  <button
                    key={bd.file}
                    type="button"
                    onClick={() => { setBgReplacementUrl(url); setBgReplacementType('image'); }}
                    className="relative aspect-video overflow-hidden flex flex-col"
                    style={{
                      padding: 6,
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-2)',
                      border: `1.5px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <img
                      src={src}
                      alt={bd.label}
                      className="flex-1 w-full object-cover"
                      style={{ borderRadius: 4 }}
                    />
                    <div className="mono-label" style={{ marginTop: 6, fontSize: 9 }}>{bd.label}</div>
                    {active && (
                      <div
                        className="absolute flex items-center justify-center"
                        style={{
                          top: 8,
                          right: 8,
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          background: 'var(--hj-blue)',
                          color: 'oklch(0.18 0.005 70)',
                        }}
                      >
                        <CheckCircle className="w-3 h-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Upload your own backdrop */}
          <section>
            <div className="mono-label" style={{ marginBottom: 10 }}>Upload your own</div>
            <div
              {...bgFileDropzone.getRootProps()}
              className="text-center cursor-pointer transition-colors"
              style={{
                padding: '32px 16px',
                borderRadius: 'var(--hj-r-md)',
                border: `1.5px dashed ${bgFileDropzone.isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
                background: bgFileDropzone.isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
              }}
            >
              <input {...bgFileDropzone.getInputProps()} />
              {bgUploadingFile ? (
                <Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
              ) : (
                <>
                  <ImagePlus
                    className="mx-auto"
                    size={28}
                    strokeWidth={1.6}
                    style={{ color: 'var(--hj-ink-3)' }}
                  />
                  <p
                    className="m-0"
                    style={{ marginTop: 10, fontSize: 13.5, color: 'var(--hj-ink-2)' }}
                  >
                    Drop an image or video, or click to browse
                  </p>
                </>
              )}
            </div>
            <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
              Want stock scenes or AI-generated backdrops?{' '}
              <button
                type="button"
                onClick={() => navigate('/background-remover')}
                style={{
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                  color: 'var(--hj-yellow-ink)',
                  fontSize: 12.5,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Open the Background Remover tool
              </button>
              {' '}for the full set of options.
            </p>
          </section>

          {/* Selected backdrop preview */}
          {bgReplacementUrl && (
            <section>
              <div className="mono-label" style={{ marginBottom: 8 }}>Selected backdrop</div>
              <div
                className="relative inline-block overflow-hidden"
                style={{
                  borderRadius: 'var(--hj-r-sm)',
                  border: '1px solid var(--hj-hairline)',
                  background: 'oklch(0.1 0.005 70)',
                }}
              >
                {bgReplacementType === 'preset' ? (
                  <div
                    style={{
                      height: 120,
                      width: 200,
                      background: bgReplacementUrl.replace('solid:', ''),
                    }}
                  />
                ) : bgReplacementType === 'video' ? (
                  <video
                    src={bgReplacementUrl}
                    className="object-cover"
                    autoPlay
                    muted
                    loop
                    style={{ height: 120 }}
                  />
                ) : (
                  <img
                    src={bgReplacementUrl}
                    alt="Background"
                    className="object-cover"
                    style={{ height: 120 }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setBgReplacementUrl('')}
                  aria-label="Remove backdrop"
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 6,
                    right: 6,
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    background: 'oklch(0 0 0 / 0.6)',
                    border: 0,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Real 3s composite preview on the user's own clip (image backdrops). */}
              {bgReplacementType === 'image' && (
                <div className="mt-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={previewBackground}
                      disabled={bgPreviewLoading}
                      className="inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
                      style={{
                        padding: '7px 14px',
                        borderRadius: 999,
                        background: 'transparent',
                        border: '1px solid var(--hj-hairline)',
                        color: 'var(--hj-ink-2)',
                        fontSize: 12.5,
                        fontFamily: 'inherit',
                        cursor: bgPreviewLoading ? 'not-allowed' : 'pointer',
                        opacity: bgPreviewLoading ? 0.5 : 1,
                      }}
                    >
                      {bgPreviewLoading ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering preview… (~1 min)</>
                      ) : (
                        <><Play className="w-3.5 h-3.5" /> Preview on your clip (3s)</>
                      )}
                    </button>
                    {bgPreviewUrl && bgPreviewSig === bgReplacementUrl && (
                      <span className="mono-label" style={{ color: 'var(--hj-green)' }}>Preview ready</span>
                    )}
                    {bgPreviewUrl && bgPreviewSig !== bgReplacementUrl && (
                      <span className="mono-label">Backdrop changed — re-preview</span>
                    )}
                  </div>
                  {bgPreviewUrl && bgPreviewSig === bgReplacementUrl && (
                    <video
                      src={bgPreviewUrl}
                      className="object-contain mt-2"
                      style={{ maxHeight: 260, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)' }}
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  )}
                </div>
              )}
            </section>
          )}

          {/* Video backdrop loop options */}
          {bgReplacementUrl &&
            bgReplacementType === 'video' &&
            bgVideoDuration !== null &&
            bgVideoDuration < getEffectiveDuration() && (
              <section
                style={{
                  padding: 14,
                  borderRadius: 'var(--hj-r-sm)',
                  border: '1px solid var(--hj-yellow)',
                  background: 'var(--hj-yellow-tint)',
                }}
              >
                <p
                  style={{
                    margin: '0 0 10px',
                    fontSize: 12.5,
                    color: 'var(--hj-ink-2)',
                  }}
                >
                  Video backdrop ({Math.round(bgVideoDuration)}s) is shorter than your video ({Math.round(getEffectiveDuration())}s).
                </p>
                <div style={{ maxWidth: 340 }}>
                  <SegmentedControl
                    options={[
                      { value: 'loop', label: 'Loop to match' },
                      { value: 'stop', label: 'Stop at end' },
                    ]}
                    active={bgVideoLoop ? 'loop' : 'stop'}
                    onChange={(v) => setBgVideoLoop(v === 'loop')}
                  />
                </div>
              </section>
            )}

          {/* Continue button moved to the toolbar / MobileActionBar — the
              shell knows the per-step advance state. */}
        </div>
      )}
    </StepCard>
  );

  // Pre-apply FFmpeg filter before advancing to Background step
  const applyFilterAndAdvance = async () => {
    // Don't apply FFmpeg filter now — defer to postRenderPipeline for speed
    // Just clear any previously applied filter state and advance
    setFilteredVideoUrl('');
    setFilteredVideoKey('');
    advanceStep();
  };

  const renderEnhanceStep = () => {
    const previewUrl = getUnfilteredVideoUrl();
    const cssFilter = buildCssFilterPreview(colorAdjustments);
    const hasAdj = Object.values(colorAdjustments).some(v => v !== 0);
    const selectedFilter = filterPreviews.find(fp => fp.key === videoFilter);
    // The real FFmpeg preview is "fresh" only while the filter/adjustments it was
    // rendered for still match the current selection.
    const colorPreviewFresh = !!colorPreviewUrl && colorPreviewSig === colorFilterSig();
    const canRealPreview = FFMPEG_PRESET_KEYS.has(videoFilter) || hasAdj;
    return (
      <StepCard>
        <StepHeader
          kicker="Step 04 · Filter"
          title="Color the room."
          subtitle="Pick a preset or push the sliders. Live preview on the right — render a real 10s sample to see the exact grade."
        />

        {/* Preview tile — real FFmpeg render when fresh, else CSS approximation */}
        {previewUrl && (
          <div
            className="relative aspect-video overflow-hidden"
            style={{
              background: 'oklch(0.1 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 12,
              maxHeight: 320,
            }}
          >
            <video
              src={colorPreviewFresh ? colorPreviewUrl! : previewUrl}
              key={colorPreviewFresh ? colorPreviewUrl! : previewUrl}
              className="w-full h-full object-contain"
              style={{ filter: colorPreviewFresh ? 'none' : cssFilter }}
              controls
              preload="auto"
              playsInline
              autoPlay
              muted
              loop
            />
            {(hasAdj || selectedFilter) && (
              <div
                className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5"
                style={{
                  padding: '3px 8px',
                  borderRadius: 4,
                  background: 'oklch(0 0 0 / 0.55)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <span className="mono-label" style={{ color: colorPreviewFresh ? 'var(--hj-green)' : 'var(--hj-blue)', fontSize: 9 }}>
                  {colorPreviewFresh ? 'Real render' : 'Approx'} · {selectedFilter?.label || 'Custom'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Render-real-preview button — the CSS tile only approximates the grade */}
        {previewUrl && (
          <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={previewColorFilter}
              disabled={colorPreviewLoading || !canRealPreview}
              className="inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
              style={{
                padding: '7px 14px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 12.5,
                fontFamily: 'inherit',
                cursor: colorPreviewLoading || !canRealPreview ? 'not-allowed' : 'pointer',
                opacity: colorPreviewLoading || !canRealPreview ? 0.5 : 1,
              }}
            >
              {colorPreviewLoading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering…</>
              ) : (
                <><Play className="w-3.5 h-3.5" /> Render real 10s preview</>
              )}
            </button>
            {colorPreviewFresh && (
              <span className="mono-label" style={{ color: 'var(--hj-green)' }}>Showing real render</span>
            )}
            {colorPreviewUrl && !colorPreviewFresh && (
              <span className="mono-label">Filter changed — re-render</span>
            )}
          </div>
        )}

        {/* Tabs: Presets / Custom */}
        <div
          className="flex"
          style={{
            borderBottom: '1px solid var(--hj-hairline-soft)',
            marginBottom: 14,
          }}
        >
          {(['presets', 'custom'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setColorGradeTab(t)}
              className="inline-flex items-center gap-1.5 capitalize transition-colors"
              style={{
                padding: '10px 14px',
                background: 'transparent',
                border: 0,
                color: colorGradeTab === t ? 'var(--hj-ink)' : 'var(--hj-ink-3)',
                borderBottom: `2px solid ${colorGradeTab === t ? 'var(--hj-blue)' : 'transparent'}`,
                marginBottom: -1,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              {t}
              {t === 'presets' && videoFilter !== 'none' && colorGradeTab !== 'presets' && (
                <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: 'var(--hj-blue)' }} />
              )}
              {t === 'custom' && hasAdj && colorGradeTab !== 'custom' && (
                <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: 'var(--hj-blue)' }} />
              )}
            </button>
          ))}
        </div>

        {/* Presets */}
        {colorGradeTab === 'presets' && (
          <>
            {filterPreviewsLoading ? (
              <div
                className="flex items-center justify-center gap-2"
                style={{ padding: '24px 0', color: 'var(--hj-ink-3)' }}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span style={{ fontSize: 13 }}>Generating previews…</span>
              </div>
            ) : filterPreviews.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {[{ key: 'none', label: 'None', description: 'Original', preview: '' }, ...filterPreviews].map((fp) => {
                  const isActive = videoFilter === fp.key;
                  return (
                    <button
                      key={fp.key}
                      type="button"
                      onClick={() => setVideoFilter(fp.key === videoFilter ? 'none' : fp.key)}
                      className="flex flex-col gap-1.5 text-left transition-colors"
                      style={{
                        padding: 6,
                        borderRadius: 'var(--hj-r-sm)',
                        background: isActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                        border: `1px solid ${isActive ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                        color: 'var(--hj-ink)',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        className="aspect-video overflow-hidden flex items-center justify-center"
                        style={{
                          background: 'oklch(0.12 0.005 70)',
                          borderRadius: 4,
                        }}
                      >
                        {fp.preview ? (
                          <img src={fp.preview} alt={fp.label} className="w-full h-full object-cover" />
                        ) : (
                          <span className="mono-label" style={{ fontSize: 9 }}>None</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--hj-ink)' }}>
                        {fp.label}
                      </div>
                      {fp.description && (
                        <div className="mono-label" style={{ fontSize: 9 }}>{fp.description}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div
                className="flex items-center justify-center gap-2"
                style={{ padding: '24px 0', color: 'var(--hj-ink-3)' }}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span style={{ fontSize: 13 }}>Loading color grades…</span>
              </div>
            )}
          </>
        )}

        {/* Custom */}
        {colorGradeTab === 'custom' && (
          <div className="flex flex-col gap-4">
            {([
              { key: 'brightness',  label: 'Brightness'  },
              { key: 'contrast',    label: 'Contrast'    },
              { key: 'saturation',  label: 'Saturation'  },
              { key: 'temperature', label: 'Temperature' },
              { key: 'exposure',    label: 'Exposure'    },
            ] as const).map(({ key, label }) => (
              <HJSlider
                key={key}
                label={label}
                value={colorAdjustments[key]}
                min={-100}
                max={100}
                step={1}
                onChange={(v) => setColorAdjustments((prev) => ({ ...prev, [key]: v }))}
              />
            ))}
            {hasAdj && (
              <button
                type="button"
                onClick={() => setColorAdjustments({ brightness: 0, contrast: 0, saturation: 0, temperature: 0, exposure: 0 })}
                className="self-start inline-flex items-center gap-1.5 mono-label"
                style={{
                  padding: '6px 10px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-3)',
                  cursor: 'pointer',
                }}
              >
                <RotateCcw className="h-3 w-3" />
                Reset all
              </button>
            )}
          </div>
        )}

        {/* Applying-filter progress bar (md+ in-card; sm uses MobileActionBar label) */}
        {applyingFilter && (
          <div style={{ marginTop: 18 }}>
            <Progress value={filterProgress} className="h-1.5" />
          </div>
        )}
      </StepCard>
    );
  };

  const renderAudioStep = () => {
    const audioModes = [
      { k: 'light',   label: 'Light',   desc: 'Levels only. No denoise — keeps the original recording intact.' },
      { k: 'natural', label: 'Natural', desc: 'Classic denoise. Preserves voice character.' },
      { k: 'strong',  label: 'Strong',  desc: 'AI isolation + dereverb. Best for noisy/echoey rooms.' },
    ] as const;
    return (
      <StepCard>
        <StepHeader
          kicker="Step 05 · Audio"
          title="Fix the sound."
          subtitle="Studio-grade voice cleanup — de-noise, de-reverb, level. Preview a 10s sample before you commit."
        />

        <label
          className="flex items-center justify-between gap-3 cursor-pointer min-h-[44px] sm:min-h-0"
          style={{ marginBottom: enhanceAudio ? 14 : 0 }}
        >
          <span className="min-w-0">
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)' }}>
              Enhance voice
            </div>
            <div style={{ fontSize: 12, color: 'var(--hj-ink)', marginTop: 2 }}>
              De-noise, level — costs 1 video token.
            </div>
          </span>
          <HJToggle on={enhanceAudio} onChange={setEnhanceAudio} ariaLabel="Enhance voice" />
        </label>

        {enhanceAudio && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {audioModes.map((m) => {
                const active = audioEnhanceMode === m.k;
                return (
                  <button
                    key={m.k}
                    type="button"
                    onClick={() => {
                      setAudioEnhanceMode(m.k as typeof audioEnhanceMode);
                      setAudioPreviewUrl(null);
                      setAudioPreviewMode(null);
                    }}
                    className="text-left transition-colors"
                    style={{
                      padding: 12,
                      borderRadius: 'var(--hj-r-sm)',
                      background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                      border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                      color: 'var(--hj-ink)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{m.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--hj-ink-2)', marginTop: 2, lineHeight: 1.4 }}>
                      {m.desc}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3" style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={previewAudioEnhancement}
                disabled={audioPreviewLoading || clips.length === 0}
                className="inline-flex items-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-2)',
                  fontSize: 12.5,
                  fontFamily: 'inherit',
                  cursor: audioPreviewLoading || clips.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: audioPreviewLoading || clips.length === 0 ? 0.5 : 1,
                }}
              >
                {audioPreviewLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing preview…</>
                ) : (
                  <><Play className="w-3.5 h-3.5" /> Preview 10s sample</>
                )}
              </button>
              {audioPreviewUrl && audioPreviewMode === audioEnhanceMode && (
                <span className="mono-label" style={{ color: 'var(--hj-green)' }}>Preview ready</span>
              )}
              {audioPreviewMode && audioPreviewMode !== audioEnhanceMode && audioPreviewUrl && (
                <span className="mono-label">Mode changed — re-preview</span>
              )}
            </div>

            {audioPreviewUrl && audioPreviewMode === audioEnhanceMode && (
              <audio
                controls
                src={audioPreviewUrl}
                className="w-full"
                style={{ marginTop: 10, height: 40 }}
              />
            )}
          </>
        )}
      </StepCard>
    );
  };

  const renderRenderStep = () => {
    const stageInfo: Record<string, { label: string; progress: number | null }> = {
      trimming:        { label: 'Trimming clips',     progress: renderProgress },
      merging:         { label: 'Merging clips',      progress: renderProgress },
      bg_removing:     { label: 'Removing background',progress: renderProgress },
      rendering:       { label: 'Rendering video',    progress: renderProgress },
      color_grading:   { label: 'Color grading',      progress: 100 },
      audio_enhancing: { label: 'Enhancing audio',    progress: 100 },
    };
    const inFlight = renderStatus in stageInfo;
    const stage = inFlight ? stageInfo[renderStatus] : null;
    const stageProgress = stage?.progress ?? renderProgress;
    const adjustmentsActive = Object.values(colorAdjustments).filter((v) => v !== 0).length;
    const summaryRows: { label: string; value: string }[] = [
      { label: 'Clips',          value: String(clips.length) },
      { label: 'Background',     value: bgOption === 'remove' ? 'Replaced' : bgOption === 'greenscreen' ? 'Green-screen matte' : 'Original' },
      { label: 'Color grade',    value: videoFilter === 'none' ? 'None' : (filterPreviews.find((fp) => fp.key === videoFilter)?.label || videoFilter) },
    ];
    if (adjustmentsActive > 0) summaryRows.push({ label: 'Adjustments', value: `${adjustmentsActive} active` });
    summaryRows.push({ label: 'Audio enhance', value: enhanceAudio ? `${audioEnhanceMode}` : 'Off' });

    return (
      <StepCard>
        <StepHeader
          kicker="Step 07 · Render"
          title="Joe's working."
          subtitle="Keep this tab open while it renders — the steps run from here in your browser, so closing the tab stops the render."
        />

        {/* Settings summary — always visible. Mono kickers + ink-2 values. */}
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
            marginBottom: 16,
          }}
        >
          <div className="mono-label" style={{ marginBottom: 8 }}>Final tape</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {summaryRows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3">
                <span style={{ fontSize: 12, color: 'var(--hj-ink-4)' }}>{row.label}</span>
                <span
                  className="truncate text-right"
                  style={{ fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* In-flight progress card */}
        {inFlight && stage && (
          <div
            style={{
              padding: '20px 22px',
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-2)',
              border: '1px solid var(--hj-hairline-soft)',
              marginBottom: 14,
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    background: 'var(--hj-blue)',
                    boxShadow: '0 0 12px var(--hj-blue)',
                    animation: 'pulse 1.4s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: 14.5, color: 'var(--hj-ink)', fontWeight: 500 }}>
                  {stage.label}
                </span>
              </div>
              <span
                className="tabular"
                style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 14, color: 'var(--hj-ink-2)' }}
              >
                {Math.min(100, Math.round(stageProgress))}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: 'var(--hj-surface-3)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, stageProgress)}%`,
                  background: 'var(--hj-blue)',
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
            <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
              <span className="mono-label">Keep this tab open</span>
              <span className="mono-label">
                {canSkipShotstack() ? 'Free render' : '1 video token'}
              </span>
            </div>
            <p
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: '1px solid var(--hj-hairline-soft)',
                fontSize: 12,
                color: 'var(--hj-ink-2)',
                lineHeight: 1.5,
              }}
            >
              Renders usually take 1.5–2.5× the video length depending on the options
              you selected — a 5-minute clip can take up to ~15 minutes. Keep this tab
              open until it finishes.
            </p>
          </div>
        )}

        {renderStatus === 'failed' && (
          <div
            style={{
              padding: 16,
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid hsl(var(--destructive) / 0.5)',
              background: 'hsl(var(--destructive) / 0.08)',
            }}
          >
            <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
              <AlertCircle className="w-5 h-5" style={{ color: 'hsl(var(--destructive))' }} />
              <p style={{ fontWeight: 600, color: 'hsl(var(--destructive))' }}>Render failed</p>
            </div>
            {renderError && (
              <p style={{ fontSize: 13, color: 'hsl(var(--destructive) / 0.85)', margin: 0, lineHeight: 1.4 }}>
                {renderError}
              </p>
            )}
          </div>
        )}

        {renderStatus === 'completed' && (
          <div
            className="flex items-center gap-3"
            style={{
              padding: '14px 16px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'oklch(0.42 0.15 145 / 0.12)',
              border: '1px solid var(--hj-green)',
              color: 'var(--hj-ink)',
            }}
          >
            <CheckCircle className="w-5 h-5" style={{ color: 'var(--hj-green)' }} />
            <div>
              <div style={{ fontWeight: 600 }}>Render complete</div>
              <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                Continue to download or save to your library.
              </div>
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const getDownloadFileName = () => {
    if (clips.length === 0) return 'enhanced_audition.mp4';
    const baseName = clips[0].name.replace(/\.[^.]+$/, ''); // Strip original extension
    return `enhanced_${baseName}.mp4`;
  };

  const handleDownload = async () => {
    const fileName = getDownloadFileName();
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);

    // iOS: use Web Share API so user gets "Save Video" option for camera roll
    if (isIOS && navigator.share) {
      try {
        // Fetch as blob and share as file — this gives "Save Video" in share sheet
        const resp = await fetch(finalVideoUrl);
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], fileName, { type: 'video/mp4' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: fileName });
            return;
          }
        }
      } catch {
        // Share cancelled or failed — fall through to URL-based share
      }

      // Fallback: share the URL (still gives Save Video on iOS 16+)
      try {
        await navigator.share({ url: finalVideoUrl, title: fileName });
        return;
      } catch {
        // User cancelled — fall through to direct download
      }
    }

    // Determine the fetch URL — proxy URLs get download=1 for Content-Disposition
    let fetchUrl = finalVideoUrl;
    if (finalVideoUrl.includes('/api/s3/')) {
      const separator = finalVideoUrl.includes('?') ? '&' : '?';
      fetchUrl = `${finalVideoUrl}${separator}download=1&filename=${encodeURIComponent(fileName)}`;
    }

    // Desktop / Android: fetch blob and trigger download
    try {
      const resp = await fetch(fetchUrl);
      if (!resp.ok) throw new Error(`${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    } catch {
      // Fetch failed (CORS or 404) — fall through to direct navigation
    }

    // Fallback: direct navigation
    if (finalVideoUrl.includes('/api/s3/')) {
      window.location.href = fetchUrl;
    } else {
      window.open(finalVideoUrl, '_blank');
    }
  };

  const renderDownloadStep = () => (
    <StepCard>
      <StepHeader
        kicker="Step 08 · Download"
        title="Tape's ready."
        subtitle="Save it to your library, share it directly, or start a new take."
      />

      {finalVideoUrl ? (
        <>
          {/* Big preview tile */}
          <div
            className="aspect-video overflow-hidden relative"
            style={{
              background: 'oklch(0.08 0.005 70)',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              marginBottom: 16,
            }}
          >
            <video
              ref={previewVideoRef}
              src={finalVideoUrl}
              className="w-full h-full object-contain"
              controls
              preload="auto"
              playsInline
            />
          </div>

          {/* Primary action row */}
          <div className="flex flex-col sm:flex-row gap-2.5" style={{ marginBottom: 18 }}>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center justify-center gap-2 transition-opacity"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--hj-blue)',
                border: '1px solid var(--hj-blue)',
                color: 'oklch(0.18 0.005 70)',
                fontSize: 15,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <Download className="w-4 h-4" />
              Save to device
            </button>
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center justify-center gap-2"
              style={{
                padding: '12px 22px',
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--hj-hairline)',
                color: 'var(--hj-ink-2)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
                cursor: 'pointer',
                minHeight: 48,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Start over
            </button>
          </div>

          {/* Save to Library */}
          {getAuthToken() && (
            <div
              style={{
                padding: 16,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-2)',
                border: '1px solid var(--hj-hairline-soft)',
              }}
            >
              <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 12 }}>
                <div>
                  <div className="mono-label" style={{ marginBottom: 4 }}>Library</div>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--hj-ink)' }}>
                    Save to your Hey Joe library
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 2 }}>
                    Stored permanently — share, download, or schedule later.
                  </div>
                </div>
              </div>

              {savedToLibrary ? (
                <div
                  className="flex items-center gap-2"
                  style={{ color: 'var(--hj-green)' }}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span style={{ fontWeight: 600, fontSize: 13.5 }}>Saved to library</span>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2.5 sm:items-end">
                  <div className="flex-1">
                    <Label className="mono-label" style={{ marginBottom: 6, display: 'block' }}>
                      Project
                    </Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={loadingProjects ? 'Loading…' : 'Select project'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p._id} value={p.name}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveToLibrary}
                    disabled={savingToLibrary}
                    className="inline-flex items-center justify-center gap-1.5 transition-opacity disabled:cursor-not-allowed"
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      background: 'var(--hj-surface-3)',
                      border: '1px solid var(--hj-hairline)',
                      color: 'var(--hj-ink)',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      cursor: savingToLibrary ? 'not-allowed' : 'pointer',
                      opacity: savingToLibrary ? 0.6 : 1,
                      minHeight: 44,
                    }}
                  >
                    {savingToLibrary ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                    ) : (
                      <><Archive className="w-3.5 h-3.5" /> Save to library</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div
          className="flex items-center gap-3"
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-sm)',
            background: 'var(--hj-surface-2)',
            border: '1px solid var(--hj-hairline-soft)',
          }}
        >
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
          <span style={{ fontSize: 13.5, color: 'var(--hj-ink-2)' }}>
            Waiting for the final render…
          </span>
        </div>
      )}
    </StepCard>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload': return renderUploadStep();
      case 'trim': return renderTrimStep();
      case 'merge': return renderMergeStep();
      case 'enhance': return renderEnhanceStep();
      case 'audio': return renderAudioStep();
      case 'background': return renderBackgroundStep();
      case 'render': return renderRenderStep();
      case 'download': return renderDownloadStep();
    }
  };

  // Shell-level Back: walk to the previous step via goToStep (which resets
  // per-step state in the existing flow — going back from trim resets trim).
  const onShellBack = () => {
    if (stepIndex > 0) goToStep(STEP_ORDER[stepIndex - 1]);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--hj-bg)' }}>
      <ToolShell
        toolName="Audition Enhancer"
        toolNumber={`Tool · ${String(stepIndex + 1).padStart(2, '0')} / ${String(STEPS.length).padStart(2, '0')}`}
        usesCredits
        steps={STEPS as ToolStep[]}
        currentStepKey={currentStep}
        completedKeys={completedSteps as Set<string>}
        onStepChange={(k) => goToStep(k as WizardStep)}
        onBack={onShellBack}
        onAdvance={currentStepAdvance.onAdvance}
        advanceLabel={currentStepAdvance.label}
        advanceDisabled={currentStepAdvance.disabled}
        showAdvance={currentStepAdvance.showAdvance !== false}
        onBackToStudio={() => navigate('/')}
      >
        {renderCurrentStep()}

        {/* Scratch Files — raw recordings/uploads from the last 30 days */}
        {currentStep === 'upload' && getAuthToken() && scratchFiles.length > 0 && (
          <div
            className="mt-6 p-4 sm:p-6"
            style={{
              background: 'var(--hj-beige)',
              border: '1px solid var(--hj-beige-line)',
              borderRadius: 'var(--hj-r-lg)',
              color: 'var(--hj-beige-ink)',
            }}
          >
            <button
              onClick={() => setShowScratch(!showScratch)}
              className="flex items-center justify-between w-full text-left"
            >
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--hj-beige-ink)' }}>Scratch Files</h2>
                <p className="text-xs" style={{ color: 'var(--hj-beige-ink-3)' }}>
                  {scratchFiles.length} recent recording{scratchFiles.length !== 1 ? 's' : ''} &amp; upload{scratchFiles.length !== 1 ? 's' : ''} — kept 30 days
                </p>
              </div>
              {showScratch
                ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-3)' }} />
                : <ChevronDown className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-3)' }} />}
            </button>

            {showScratch && (() => {
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
              const yesterday = today - 86400000;
              const groupLabel = (iso: string) => {
                const d = new Date(iso);
                const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                if (dayStart === today) return 'Today';
                if (dayStart === yesterday) return 'Yesterday';
                return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
              };
              const groups: Record<string, typeof scratchFiles> = {};
              for (const sf of scratchFiles) {
                const label = groupLabel(sf.createdAt);
                (groups[label] ||= []).push(sf);
              }
              return (
                <div className="mt-4 space-y-4">
                  {Object.entries(groups).map(([label, files]) => (
                    <div key={label}>
                      <h3
                        className="text-xs font-semibold uppercase tracking-wide mb-2"
                        style={{ color: 'var(--hj-beige-ink-3)' }}
                      >
                        {label}
                      </h3>
                      <div className="space-y-2">
                        {files.map(sf => {
                          const bucketPrefix = sf.bucket && sf.bucket !== 'hey-joe-videos' ? `${sf.bucket}/` : '';
                          const proxyUrl = `/api/s3/${bucketPrefix}${sf.s3Key}`;
                          const sizeMB = sf.fileSize ? (sf.fileSize / (1024 * 1024)).toFixed(1) : null;
                          const time = new Date(sf.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                          return (
                            <div
                              key={sf._id}
                              className="flex items-center gap-3 p-2 transition-colors"
                              style={{
                                background: 'var(--hj-beige-hover)',
                                border: '1px solid var(--hj-beige-line)',
                                borderRadius: 'var(--hj-r-md)',
                              }}
                            >
                              <button
                                onClick={() => { setLibraryVideoUrl(proxyUrl); setLibraryVideoName(sf.fileName); }}
                                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                              >
                                <div
                                  className="w-16 h-10 flex items-center justify-center flex-shrink-0 overflow-hidden"
                                  style={{
                                    background: 'var(--hj-beige-deep)',
                                    borderRadius: 'var(--hj-r-xs)',
                                  }}
                                >
                                  <VideoThumbnail
                                    src={proxyUrl}
                                    fallbackIcon={<Video className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate" style={{ color: 'var(--hj-beige-ink)' }}>
                                    {sf.fileName}
                                  </p>
                                  <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--hj-beige-ink-3)' }}>
                                    <span>{time}</span>
                                    {sizeMB && <span>• {sizeMB} MB</span>}
                                    <span
                                      className="px-1.5 py-0.5 capitalize"
                                      style={{
                                        background: 'var(--hj-beige-deep)',
                                        color: 'var(--hj-beige-ink-2)',
                                        borderRadius: 'var(--hj-r-xs)',
                                      }}
                                    >
                                      {sf.source}
                                    </span>
                                  </div>
                                </div>
                              </button>
                              <button
                                onClick={() => reattachScratchFile(sf)}
                                className="text-xs px-3 py-1.5 hover:opacity-90 flex-shrink-0"
                                style={{
                                  background: 'var(--hj-blue)',
                                  color: 'oklch(0.18 0.005 70)',
                                  borderRadius: 'var(--hj-r-sm)',
                                  fontWeight: 600,
                                }}
                              >
                                Use
                              </button>
                              <button
                                onClick={() => deleteScratchFile(sf._id)}
                                className="flex-shrink-0"
                                style={{ color: 'var(--hj-beige-ink-3)' }}
                                title="Delete"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Your Library — saved renders. Always render when logged in, even
            empty, so the section is discoverable. Beige paper treatment so it
            sits alongside Scratch Files with equal visual weight. */}
        {getAuthToken() && (
          <div
            className="mt-6 p-4 sm:p-6"
            style={{
              background: 'var(--hj-beige)',
              border: '1px solid var(--hj-beige-line)',
              borderRadius: 'var(--hj-r-lg)',
              color: 'var(--hj-beige-ink)',
            }}
          >
            <button
              onClick={() => setShowLibrary(!showLibrary)}
              className="flex items-center justify-between w-full text-left"
            >
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--hj-beige-ink)' }}>
                  Your Library
                </h2>
                <p className="text-xs" style={{ color: 'var(--hj-beige-ink-3)' }}>
                  {libraryItems.length === 0
                    ? 'Saved renders show here'
                    : `${libraryItems.length} saved video${libraryItems.length !== 1 ? 's' : ''}`}
                </p>
              </div>
              {showLibrary
                ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-3)' }} />
                : <ChevronDown className="w-5 h-5" style={{ color: 'var(--hj-beige-ink-3)' }} />}
            </button>

            {showLibrary && libraryItems.length === 0 && (
              <div
                className="mt-4 text-center"
                style={{
                  padding: '24px 16px',
                  borderRadius: 'var(--hj-r-md)',
                  border: '1.5px dashed var(--hj-beige-line)',
                  background: 'var(--hj-beige-hover)',
                  color: 'var(--hj-beige-ink-2)',
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                Finish a render and tap "Save to library" — it'll show up here
                so you can pull it into another take later.
              </div>
            )}

            {showLibrary && libraryItems.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {libraryItems.map(item => {
                  const proxyUrl = item.savedS3Key
                    ? `/api/s3/${item.savedS3Key}`
                    : item.savedS3Url;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { setLibraryVideoUrl(proxyUrl); setLibraryVideoName(item.name); }}
                      className="group block transition-colors overflow-hidden text-left"
                      style={{
                        borderRadius: 'var(--hj-r-md)',
                        border: '1px solid var(--hj-beige-line)',
                        background: 'var(--hj-beige-hover)',
                      }}
                    >
                      <div
                        className="aspect-video flex items-center justify-center relative"
                        style={{ background: 'var(--hj-beige-deep)' }}
                      >
                        <VideoThumbnail
                          src={proxyUrl}
                          initialThumbnail={item.thumbnailUrl}
                          fallbackIcon={<Video className="w-8 h-8" style={{ color: 'var(--hj-beige-ink-3)' }} />}
                          onGenerated={(dataUrl) => {
                            if (item.projectId) {
                              const authToken = getAuthToken();
                              if (authToken) {
                                fetch(`/api/projects/${item.projectId}/render-thumbnail`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                                  body: JSON.stringify({ renderId: item.id, thumbnailUrl: dataUrl }),
                                }).catch(() => {});
                              }
                            }
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: 'var(--hj-blue)' }}
                          >
                            <Play className="w-5 h-5 ml-0.5" style={{ color: 'oklch(0.18 0.005 70)' }} />
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: 8 }}>
                        <p
                          className="truncate m-0"
                          style={{ fontSize: 12, fontWeight: 500, color: 'var(--hj-beige-ink)' }}
                        >
                          {item.name}
                        </p>
                        <div className="flex items-center gap-1" style={{ marginTop: 2 }}>
                          {item.projectName && (
                            <span
                              className="truncate max-w-[80px]"
                              style={{
                                fontSize: 10,
                                padding: '1px 6px',
                                borderRadius: 'var(--hj-r-xs)',
                                background: 'var(--hj-beige-deep)',
                                color: 'var(--hj-beige-ink-2)',
                              }}
                            >
                              {item.projectName}
                            </span>
                          )}
                          <span style={{ fontSize: 10, color: 'var(--hj-beige-ink-3)' }}>
                            {item.savedAt ? new Date(item.savedAt).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Casting Networks integration — quiet disclosure row below the
            library; the section supplies its own subtle collapsed treatment. */}
        {getAuthToken() && <CastingNetworksSection />}
      </ToolShell>

      {/* Library Video Player Modal */}
      {libraryVideoUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLibraryVideoUrl(null)}
        >
          <div
            className="relative w-full max-w-3xl mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-white truncate mr-4">{libraryVideoName}</p>
              <div className="flex items-center gap-2">
                <a
                  href={`${libraryVideoUrl}?download=1`}
                  className="text-xs text-white/70 hover:text-white flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
                <button
                  onClick={() => setLibraryVideoUrl(null)}
                  className="text-white/70 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              <video
                src={libraryVideoUrl}
                className="w-full h-full object-contain"
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>
        </div>
      )}

      <CreditConfirmDialog />
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} trigger="ai_feature" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <RecordingModal
        open={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        onSave={(file) => onDrop([file], 'recording')}
      />
    </div>
  );
};